import Phaser from 'phaser';
import SpriteButton from 'ui/spriteButton';
import { GlobalAbilityButton } from 'ui/abilityButton';
import { buildLevel, addPieceSpriteToBoard } from 'helpers/gameplay';
import { determineBounceBack } from 'helpers/levelHelpers';
import * as MapHelpers from 'helpers/mapHelpers';
import * as Movement from 'helpers/movementHelpers';
import * as History from 'helpers/historyHelpers';
import * as AI from 'helpers/ai';
import * as SharedAI from 'helpers/ai-shared';
import { addRecorder } from 'helpers/recorder';
import {
  isInFreeRoam,
  endSceneAndChildScenes,
  toggleSwitchAt
} from 'helpers/sceneHelpers';
import { debounce } from 'states/components/LoadLevel';
import {
  initializeControllerInput,
  controllerInputUpdate,
  controllerInputForMenuDirections,
  getAnalogueDirection
} from 'helpers/controller';
import { recordPointerTelemetry } from 'helpers/inputTelemetry';
import { getDirection } from 'helpers/abilities';
import { logicIndexToSpriteIndex } from 'helpers/sharedHelpers';
import {
  loadProgress,
  addGoldToPlayer,
  savePlayerPosition,
  equipWeaponToPiece,
  saveWorldProgress,
  equipWeaponToPieceByID
} from 'helpers/player';
import { saveStoryProgress } from 'helpers/player-browser';
import {
  cursorCSS,
  cursorHandCSS,
  randomName,
  ZOOM_LEVELS,
  BLUE_PIECES,
  RED_PIECES,
  COLORLESS_PIECES,
  SMALL_PIECES,
  ITEMS,
  WALKABLE,
  UNWALKABLE,
  SHADOW,
  KEY,
  LOCKED_DOOR,
  SWITCH_OPEN_TOP,
  SWITCH_OPEN_BOTTOM,
  SWITCH_CLOSED_TOP,
  SWITCH_CLOSED_BOTTOM,
  GATE_HORIZONTAL_BOTTOM,
  GATE_VERTICAL_BOTTOM,
  GATE_VERTICAL_MIDDLE,
  GATE_CORNER_TOP_LEFT_BOTTOM,
  GATE_CORNER_TOP_RIGHT_BOTTOM,
  GATE_CORNER_BOTTOM_LEFT_BOTTOM,
  GATE_CORNER_BOTTOM_RIGHT_BOTTOM,
  WARP,
  MAP,
  LOGIC_DOOR,
  PIECE_SHADOW,
  GOLD_TILES,
  DOORS,
  TARGET_TILE,
  HIGHLIGHT_DOOR,
  HIGHLIGHT_DOOR_HOVER,
  HIGHLIGHT_NPC,
  HIGHLIGHT_NPC_HOVER,
  HIGHLIGHT_STAIRS_UP,
  HIGHLIGHT_STAIRS_UP_HOVER,
  HIGHLIGHT_STAIRS_DOWN,
  HIGHLIGHT_STAIRS_DOWN_HOVER,
  staffUsers,
  FAYETTE_MOVEMENT_PROPERTIES,
  SIDEBAR_WIDTH
} from 'helpers/constants';

import { getStoryMapByID, recordEvent } from 'helpers/server_connection';

import ALL_ITEMS from 'items/all_items';
import { itemIDsBySpriteIndex } from 'items/all_items_meta';
import {
  checkAndApplyIdleAnimation,
  swapPieceSpriteForMovementSprite
} from 'helpers/pieceHelpers';

const onDEV = typeof __DEV__ !== 'undefined';

const AUTHORED_GATE_BOTTOM_TILES = [
  GATE_HORIZONTAL_BOTTOM,
  GATE_VERTICAL_BOTTOM,
  GATE_VERTICAL_MIDDLE,
  GATE_CORNER_TOP_LEFT_BOTTOM,
  GATE_CORNER_TOP_RIGHT_BOTTOM,
  GATE_CORNER_BOTTOM_LEFT_BOTTOM,
  GATE_CORNER_BOTTOM_RIGHT_BOTTOM
];

const HIGHLIGHTABLES = [
  613,
  614,
  615,
  HIGHLIGHT_DOOR,
  HIGHLIGHT_NPC,
  HIGHLIGHT_STAIRS_UP,
  HIGHLIGHT_STAIRS_DOWN
];

// [225][270][315]
// [180][   ][ 0 ]
// [135][ 90][ 45]

// dust should point the oppsite direction of the input
// diagonals are placeholder right now, not set properly
// note format: input Direction -> Dust Emitter Angle (opposite direction)
const DUST_EMITTER_ANGLES = {
  [-1]: {
    [-1]: { min: 0, max: 90 }, // up/left -> down/right
    0: { min: 330, max: 350 }, // left -> right
    1: { min: -90, max: 0 } // down/left -> up/right
  },
  0: {
    [-1]: { min: 60, max: 120 }, // up -> down
    1: { min: 240, max: 300 } // down -> upF
  },
  1: {
    [-1]: { min: 90, max: 180 }, // up/right -> down/left
    0: { min: 190, max: 210 }, // right -> left
    1: { min: 180, max: 270 } // down/right -> up/left
  }
};

const gameOverControllerMenuOptions = {
  undo: {
    button: null,
    directions: {
      up: 'main_menu',
      down: 'retry'
    }
  },
  retry: {
    button: null,
    directions: {
      up: 'undo',
      down: 'overworld_freeplay'
    }
  },
  overworld_freeplay: {
    button: null,
    directions: {
      up: 'retry',
      down: 'main_menu'
    }
  },
  main_menu: {
    button: null,
    directions: {
      up: 'overworld_freeplay',
      down: 'undo'
    }
  }
};

const createSharedGameOverControllerMenuOptions = () => ({
  undo: {
    button: null,
    directions: {
      up: 'retry',
      down: 'retry'
    }
  },
  retry: {
    button: null,
    directions: {
      up: 'undo',
      down: 'undo'
    }
  }
});

const createBattleResultsControllerMenuOptions = () => ({
  retry: {
    button: null,
    disabled: true,
    directions: {
      left: 'continue',
      right: 'continue'
    }
  },
  continue: {
    button: null,
    directions: {
      left: 'retry',
      right: 'retry'
    }
  }
});

const createFreeplayBattleResultsControllerMenuOptions = () => ({
  freeplay: {
    button: null,
    directions: {
      up: 'main_menu',
      down: 'main_menu',
      left: 'main_menu',
      right: 'main_menu'
    }
  },
  main_menu: {
    button: null,
    directions: {
      up: 'freeplay',
      down: 'freeplay',
      left: 'freeplay',
      right: 'freeplay'
    }
  }
});

export default class GameScene extends Phaser.Scene {
  constructor(config = 'GameScene') {
    super(config);

    this.startInput = this.startInput.bind(this);
    this.battleStart = this.battleStart.bind(this);
    this.moveUnitToTile = this.moveUnitToTile.bind(this);
    this.checkNextTurn = this.checkNextTurn.bind(this);
    this.setEventListeners = this.setEventListeners.bind(this);
    this.highlightUnitMoveset = this.highlightUnitMoveset.bind(this);
    this.genericPointerDown = this.genericPointerDown.bind(this);

    this.captured = {
      blue: [],
      red: []
    };
    this.numUndosUsed = 0;
    this.numGroundItemsPickedUp = 0;
  }
  init(data) {
    this.staffPlaying = staffUsers.includes(this.game.player.name);
    if (data.level) {
      throw new Error('GameScene requires explicit levelData.');
    }
    if (data.levelData) {
      this.levelData = data.levelData;
    }
    this.freeplayHighscore = data.freeplayHighscore;
    this.freeplayLevelIndex = data.freeplayLevelIndex;
    this.freeplayParty = data.freeplayParty;
    this.freeplayMap = data.freeplayMap;
    this.sharedLevelMode = data.sharedLevelMode;
    this.zoomLevel = 1;
    this.clearSceneThisSettings();
    this.clearCurrentMenuOptions();
    this.scale.on('resize', this.resize);
    this.events.on('shutdown', () => {
      this.scale.off('resize', this.resize);
    });
    window.aiWorker.onmessage = e => {
      if (e && e.data && e.data.type === 'ai-response') {
        this.handleAIResponse(e.data);
      }
    };
  }

  clearSceneThisSettings = () => {
    this.captured = {
      blue: [],
      red: []
    };
    this.numUndosUsed = 0;
    this.numGroundItemsPickedUp = 0;
    this.persistObtainedItemsOnReplay = false;
    this.lastTileHighlighted = null;
    this.currentSelectedPiece = null;
    this.previousSelectedPiece = null;
    this.player = null;
  };
  handleAIResponse = ({ moveScores, start, color, possibleMoves, rootMap }) => {
    History.applyMapState(this, this.map, null, rootMap, true);

    let lowest = 10000;
    let highest = -10000;
    let worstScores = [];
    let worstScoreWithAKill;
    if (color === 'red') {
      moveScores.forEach((score, index) => {
        if (score < lowest) {
          lowest = score;
          worstScores = [{ score, index }];
        } else if (score === lowest) {
          worstScores.push({ score, index });
        }
      });
      worstScoreWithAKill = worstScores.find(score => {
        return possibleMoves[score.index].move.index > -1;
      });
    } else {
      moveScores.forEach((score, index) => {
        if (score > highest) {
          highest = score;
          worstScores = [{ score, index }];
        } else if (score === highest) {
          worstScores.push({ score, index });
        }
      });
      worstScoreWithAKill = worstScores.find(score => {
        return possibleMoves[score.index].move.index > -1;
      });
    }

    const worstScore =
      color === 'red' ? Math.min(...moveScores) : Math.max(...moveScores);
    const worstIndex = moveScores.indexOf(worstScore);
    const bestMove =
      (worstScoreWithAKill && possibleMoves[worstScoreWithAKill.index]) ||
      possibleMoves[worstIndex];

    if (window.ai.debug) {
      console.log('--------------');
      console.log('AI Type:', window.ai.method);
      console.log('PC Move color:', color);
      console.log('PC Move possibles:', possibleMoves);
      console.log('PC Move scores:', moveScores);
      console.log('PC Move worst:', worstScores);
      console.log('PC worstScoreWithAKill', worstScoreWithAKill);
      console.log('PC Move worstIndex:', worstIndex);
      console.log('PC Move best move:', bestMove);
      console.log('time:', new Date() - start);
      console.log('--------------');
    }

    // return bestMove;
    const enColor = color === 'blue' ? 'red' : 'blue';
    const menuScene = this.scene.get('GameMenuScene');
    if (bestMove) {
      this.pcMove(bestMove);
      menuScene.setTurnNumber(menuScene.turnNumber + 1);
    } else {
      menuScene.setTurnColor(enColor);
      menuScene.setTurnNumber(menuScene.turnNumber + 1);
      if (this.cpuSimulation) {
        this.getCpuMove(enColor);
      } else {
        this.playerTurn();
      }
    }
  };
  resize = () => {
    const dialogue = this.scene.isActive('DialogueScene');
    if (!dialogue) {
      this.setZoomLevel();
      this.cameras.main.centerOn(this.stageCenter.x, this.stageCenter.y);
    }
  };
  async create() {
    this.captured = {
      blue: [],
      red: []
    };
    if (this.pendingLevelData) {
      await this.pendingLevelData;
    }
    if (this.staffPlaying && onDEV && this.levelData.id) {
      getStoryMapByID(this.levelData.id)
        .then(res => {
          if (res && res.lvlData) {
            this.levelData.lvlData = res.lvlData;
          }
          this.createGameLevel();
        })
        .catch(err => {
          this.createGameLevel();
        });
    } else {
      this.createGameLevel();
    }
  }
  createGameLevel() {
    // init - resetHistory, pieces groups, buildLevel, save first map state
    this.pieceParticles = this.add.particles('red_piece_parts');
    this.pieceParticles.setDepth(5);

    History.resetHistory(this);
    this.pieces = this.add.group();
    this.animations = this.add.group();
    this.pieces.setDepth(4);
    buildLevel(this, false);
    this.setupAuthoredPuzzleSwitches();
    History.saveMapState(this, 'blue');

    this.scene.launch('GameMenuScene', {
      level: this.levelData,
      sceneRef: this,
      hideObjective: true,
      onReady: () => {
        this.scene
          .get('GameMenuScene')
          .setObjectiveText(`by: ${this.levelData.author}`);
        this.scene.get('GameMenuScene').showObjective();
        this.battleStart();
      }
    });

    let cam = this.cameras.main;
    this.stageCenter = {
      x: (this.levelData.lvlData.ground[0].length / 2 + 1) * 32,
      y: (this.levelData.lvlData.ground.length / 2 + 1) * 32
    };
    cam.centerOn(this.stageCenter.x, this.stageCenter.y);
    this.scene.launch('ZoomControls', {
      camera: this.cameras.main,
      default: this.getZoomLevel()
    });

    this.preconfigure();
    this.game.soundManager.transitionToTrack(
      this,
      this.levelData.startingMusic || 'music_battle',
      'bgMusic'
    );

    this.updatePieceSprites();
    this.createHPUI();
    this.createPieceInfoUI();
  }
  ensureSwitchAnimations = () => {
    if (!this.anims.exists('switch-on')) {
      this.anims.create({
        key: 'switch-on',
        frames: this.anims.generateFrameNumbers('anim_switch_on'),
        frameRate: 2.5,
        yoyo: false,
        repeat: -1
      });
    }
    if (!this.anims.exists('switch-off')) {
      this.anims.create({
        key: 'switch-off',
        frames: this.anims.generateFrameNumbers('anim_switch_off'),
        frameRate: 2.5,
        yoyo: false,
        repeat: -1
      });
    }
  };
  clearAuthoredPuzzleSwitches = () => {
    if (this.authoredSwitchSprites) {
      this.authoredSwitchSprites.forEach(sprite => {
        if (sprite && sprite.destroy) {
          sprite.destroy();
        }
      });
    }
    this.authoredSwitchSprites = [];
  };
  setAuthoredSwitchTilesVisible = (x, y, visible) => {
    [
      {
        layer: 'below_player',
        y,
        indexes: [SWITCH_OPEN_BOTTOM, SWITCH_CLOSED_BOTTOM]
      },
      {
        layer: 'above_player',
        y: y - 1,
        indexes: [SWITCH_OPEN_TOP, SWITCH_CLOSED_TOP]
      }
    ].forEach(tileData => {
      const tile = this.map.getTileAt(x, tileData.y, true, tileData.layer);
      if (!tile || !tileData.indexes.includes(tile.index)) {
        return;
      }
      tile.visible = visible;
      tile.alpha = visible ? 1 : 0;
    });
  };
  setupAuthoredPuzzleSwitches = () => {
    if (!this.map || this.editToolsScene?.editMode) {
      return;
    }

    this.clearAuthoredPuzzleSwitches();
    this.ensureSwitchAnimations();
    this.authoredGateTiles = [];
    this.authoredGatesOpen = false;

    this.map.forEachTile(
      tile => {
        if (!AUTHORED_GATE_BOTTOM_TILES.includes(tile.index)) {
          return;
        }

        const topTile = this.map.getTileAt(
          tile.x,
          tile.y - 1,
          true,
          'above_player'
        );
        this.authoredGateTiles.push({
          x: tile.x,
          y: tile.y,
          belowIndex: tile.index,
          topIndex: topTile?.index ?? -1
        });
        this.map.putTileAt(UNWALKABLE, tile.x, tile.y, true, 'logic_layer');
      },
      this,
      0,
      0,
      this.map.width,
      this.map.height,
      undefined,
      'below_player'
    );

    this.map.forEachTile(
      tile => {
        if (![SWITCH_OPEN_BOTTOM, SWITCH_CLOSED_BOTTOM].includes(tile.index)) {
          return;
        }

        const closed = tile.index === SWITCH_CLOSED_BOTTOM;
        const animKey = closed ? 'switch-on' : 'switch-off';
        const textureKey = closed ? 'anim_switch_on' : 'anim_switch_off';
        const sprite = this.add
          .sprite(tile.x * 32 + 48, tile.y * 32 + 32, textureKey)
          .setDepth(this.sys.config === 'WorldScene' ? 11 : 3);
        sprite.anims.load(animKey);
        sprite.anims.play(animKey);
        sprite.puzzleSwitch = true;

        const logicTile = this.map.putTileAt(
          closed ? UNWALKABLE : WALKABLE,
          tile.x,
          tile.y,
          true,
          'logic_layer'
        );
        logicTile.occupant = sprite;
        this.setAuthoredSwitchTilesVisible(tile.x, tile.y, false);
        this.authoredSwitchSprites.push(sprite);
      },
      this,
      0,
      0,
      this.map.width,
      this.map.height,
      undefined,
      'below_player'
    );
  };
  toggleAuthoredGates = open => {
    if (!this.authoredGateTiles || !this.authoredGateTiles.length) {
      return false;
    }

    this.authoredGateTiles.forEach(gate => {
      if (open) {
        this.map.removeTileAt(gate.x, gate.y, false, true, 'logic_layer');
        this.map.removeTileAt(gate.x, gate.y, false, true, 'below_player');
        if (gate.topIndex > -1) {
          this.map.removeTileAt(
            gate.x,
            gate.y - 1,
            false,
            true,
            'above_player'
          );
        }
      } else {
        this.map.putTileAt(UNWALKABLE, gate.x, gate.y, true, 'logic_layer');
        this.map.putTileAt(
          gate.belowIndex,
          gate.x,
          gate.y,
          true,
          'below_player'
        );
        if (gate.topIndex > -1) {
          this.map.putTileAt(
            gate.topIndex,
            gate.x,
            gate.y - 1,
            true,
            'above_player'
          );
        }
      }
    });

    this.authoredGatesOpen = open;
    return true;
  };
  handleAuthoredSwitchHit = pieceTile => {
    const sprite = pieceTile?.occupant;
    if (!sprite?.puzzleSwitch) {
      return false;
    }

    const open = toggleSwitchAt(this, pieceTile.x, pieceTile.y, sprite);
    if (this.toggleAuthoredGates(open)) {
      this.game.soundManager.playSfx('gateRumble');
      this.game.soundManager.playSfx('gateCreak');
      this.cameras.main.shake(650, 0.0001);
    }
    return true;
  };
  preconfigure() {
    if (!this.abilityButton || !this.abilityButton.scene) {
      this.abilityButton = GlobalAbilityButton(this);
    }
    //ai
    const numPieces =
      this.scene?.scene?.inBattleLevelData?.piecesCount ||
      this.pieces.getChildren().filter(pieceOrItem => {
        return pieceOrItem.parentContainer.getData('type') === 'piece';
      }).length;
    if (numPieces > 12) {
      window.ai['future looking limit'] = 2;
      window.ai.method = 'minimax';
    } else if (numPieces > 6) {
      window.ai['future looking limit'] = 3;
      window.ai.method = 'minimax';
    } else if (numPieces > 2) {
      window.ai['future looking limit'] = 4;
      window.ai.method = 'minimax';
    } else {
      // 1v1
      window.ai.method = 'swarm';
    }

    const lvlAiMethod = this.scene?.scene?.inBattleLevelData?.aiMethod;
    if (lvlAiMethod) {
      window.ai.method = lvlAiMethod;
    }

    this.game.updateDatgui?.();
  }
  battleStart() {
    this.preconfigure();
    // count starting items
    const pieces = this.pieces.getChildren();
    const startingItemIDs = pieces
      .filter(piece => piece.color === 'blue' && piece.itemID)
      .reduce((acc, piece) => {
        acc[piece.id] = piece.itemID;
        return acc;
      }, {});
    this.itemsAtStart = startingItemIDs;
    if (this.levelData.combat && !this.replay) {
      this.timeLevelStarted = new Date().getTime();
      const menuScene = this.scene.get('GameMenuScene');
      menuScene.showBattleStart(this.startInput);
      this.battlingInStory = true;
      this.updatePieceSprites();
    } else {
      if (!this.replay && !this.levelData.combat) {
        this.levelData.npcs &&
          this.levelData.npcs.forEach(npc => {
            let tile = this.map.putTileAt(
              WALKABLE,
              npc.x,
              npc.y,
              false,
              'logic_layer'
            );
            tile.npc = true;
            tile.action = npc.action;
          });
      }
      this.startInput();
    }
  }
  createPieceInfoUI() {
    // piece info random names
    let partyList = loadProgress().party;
    let pieceCount = 100;
    this.pieceMap = this.pieces.getChildren().reduce((map, piece) => {
      let member = partyList.find((partyMember, index) => {
        if (piece.id) {
          return partyMember.id === piece.id;
        }
        const isFayette = [283, 284].includes(piece.tile.index);
        if (isFayette) {
          return partyMember.name === 'Fayette';
        }
        const isUncle = piece.tile.index === 239;
        if (isUncle) {
          return partyMember.name === 'Uncle';
        }
        const isBluePiece = BLUE_PIECES.includes(piece.tile.index);
        return (
          isBluePiece &&
          partyMember.rank === (piece.tile.index - 271) % 6 &&
          partyMember.id !== 1
        );
      });
      if (member) {
        piece.id = member.id;
        piece.name = member.name;
        map[member.id] = member;
        return map;
      }
      if (
        BLUE_PIECES.concat(RED_PIECES, COLORLESS_PIECES).includes(
          piece.tile.index
        )
      ) {
        let id = pieceCount;
        pieceCount += 1;
        piece.id = id;
        let rankIndexBase = piece.color === 'blue' ? 271 : 307;
        if (piece.tile.index === 235) {
          rankIndexBase = 235;
        }
        map[id] = {
          id,
          main: false,
          name: randomName(),
          rank: (piece.tile.index - rankIndexBase) % 6,
          hp: piece.hp || 1,
          x: piece.tile.x,
          y: piece.tile.y
        };
        return map;
      } else {
        return map;
      }
    }, {});
    this.scene.launch('PieceInfoScene', { scene: this });
    this.pieceInfoUI = this.game.scene.keys['PieceInfoScene'];
    History.resetHistory(this);
    History.saveMapState(this, 'blue');
  }
  addDevTools() {
    if (!this.logicLayerTools && this.game.datgui) {
      this.logicLayerTools = this.game.datgui.addFolder('Logic Layer');
      this.logicLayerTools.add(
        this.map.layers[4].tilemapLayer,
        'alpha',
        0,
        1,
        0.1
      );
    }

    this.recorderFolder = addRecorder(this, this.game.datgui);

    this.game.showControllerDebug = false;
    const folders = this.game.datgui?.__folders;
    if (folders && !folders['Debug Controller']) {
      this.debugControllerFolder = this.game.datgui.addFolder(
        'Debug Controller'
      );
      this.debugControllerFolder.add(this.game, 'showControllerDebug');
    }
  }
  createHPUI() {
    this.hpGroup = this.add.group();
    this.hpBG = this.add.graphics();
    this.hpBG.fillStyle(0x000000);
    this.hpBG.fillRect(0, 0, 50, 20);
    this.hpBG.setAlpha(0).setDepth(10);
    this.hpGroup.add(this.hpBG);
    this.hp1 = this.add.graphics();
    this.hp1.fillStyle(0xdd2323);
    this.hp1.fillRect(4, 4, 19, 12);
    this.hp1.setAlpha(0).setDepth(10);
    this.hpGroup.add(this.hp1);
    this.hp2 = this.add.graphics();
    this.hp2.fillStyle(0xdd2323);
    this.hp2.fillRect(27, 4, 19, 12);
    this.hp2.setAlpha(0).setDepth(10);
    this.hpGroup.add(this.hp2);
    //gmas amulet animation
    this.amuletCrystal = this.add
      .sprite(0, 0, 'amulet_defense_sprite')
      .setDepth(11);
    var config = {
      key: 'amulet-once-thru',
      frames: this.anims.generateFrameNumbers('amulet_defense_sprite'),
      frameRate: 8,
      yoyo: false,
      repeat: 0
    };

    this.amuletAnim = this.anims.create(config);
    this.amuletCrystal.anims.load('amulet-once-thru');
    this.amuletCrystal.setAlpha(0);
    this.amuletCrystal.on(
      'animationcomplete',
      (anim, frame) => {
        this.amuletCrystal.setAlpha(0);
      },
      this.amuletCrystal
    );
    // to use:
    // this.amuletCrystal.setPosition(tile.x, tile.y);
    // this.amuletCrystal.setAlpha(1);
    // this.amuletCrystal.anims.play('arrow-bounce');
  }
  setEventListeners() {
    this.shadow1 = this.add.sprite(0, 0, 'master_atlas', PIECE_SHADOW - 1);
    this.shadow2 = this.add.sprite(0, 0, 'master_atlas', PIECE_SHADOW - 1);
    this.shadow1.setAlpha(0).setDepth(3);
    this.shadow2.setAlpha(0).setDepth(3);
    if (!this.selectedArrow || !this.selectedArrow.scene) {
      this.selectedArrow = this.add
        .sprite(0, 0, 'ui_select_arrow')
        .setDepth(11);
      var config = {
        key: 'arrow-bounce',
        frames: this.anims.generateFrameNumbers('ui_select_arrow'),
        frameRate: 10,
        yoyo: false,
        repeat: -1
      };

      this.arrowAnim = this.anims.create(config);
      this.selectedArrow.anims.load('arrow-bounce');
      this.selectedArrow.anims.play('arrow-bounce');
    }
    const cam = this.cameras.main;
    this.cursorPointer = this.add
      .sprite(cam.worldView.centerX, cam.worldView.centerY, `cursor_pointer_1x`)
      .setOrigin(0)
      .setAlpha(0)
      .setDepth(25);
    // this.game.controllerInput = false;

    this.selectedArrow.setAlpha(0);
    this.input.on('pointerup', this.genericPointerDown);
    this.input.on('pointermove', this.mousemove);
    this.input.on('wheel', this.mousewheel);
    // the initializer returns the remove function
    const [
      addControllerInputs,
      removeControllerInputs
    ] = initializeControllerInput(
      this,
      this.moveInDirection,
      this.APressed,
      this.APressed,
      () => {},
      this.NumPressed,
      this.EPressed,
      this.BPressed,
      this.XPressed,
      this.LBPressed,
      this.RBPressed,
      this.StartPressed,
      this.EscapePressed
    );

    this.addControllerInputs = addControllerInputs;
    this.removeControllerInputs = removeControllerInputs;

    this.disableInput = false;
  }
  disableKeyboardKeys = () => {
    this.removeControllerInputs();
  };
  enableKeyboardKeys = () => {
    this.addControllerInputs();
  };

  removeEventListeners = () => {
    this.input.off('pointerup', this.genericPointerDown);
    this.removeControllerInputs && this.removeControllerInputs();
  };

  getPieceChildren = () => {
    if (!this.pieces || !this.pieces.children?.entries) {
      return [];
    }
    return this.pieces.getChildren();
  };

  isDirectInputAllowed = () => {
    if (
      !this.disableInput &&
      !this.inCutscene &&
      this.currentSelectedPiece?.tile
    ) {
      const bluePieces = this.getPieceChildren().filter(
        p => p.color === 'blue'
      );
      const inBattle =
        this.battlingInStory ||
        this.battlingInWorld ||
        this.scene.key === 'CastleScene';

      let directInputPieces = [271, 283];
      if (!inBattle) {
        directInputPieces.push(239, 273);
      }
      if (
        this.currentSelectedPiece &&
        directInputPieces.includes(this.currentSelectedPiece.tile.index) &&
        bluePieces.length === 1
      ) {
        return true;
      }
    }
  };

  isOnlyPawnInBattle = () => {
    const bluePieces = this.getPieceChildren().filter(p => p.color === 'blue');
    let pawnOrFayetteIndexes = [271, 283];
    return (
      bluePieces.length === 1 &&
      pawnOrFayetteIndexes.includes(bluePieces[0].tile.index) &&
      this.battlingInStory
    );
  };

  moveInDirection = direction => {
    const partyMenu = this.scene.get('PartyMenu');
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (partyMenuActive) {
      const consumed = partyMenu.moveCursor(direction);
      if (consumed) {
        return;
      }
    }
    if (this.currentControllerMenuOptions) {
      return this.moveInDirectionGameOverMenu(direction);
    }
    //direct control
    if (this.isDirectInputAllowed()) {
      const isBishop = this.currentSelectedPiece.tile.index === 273;
      const isUncle = this.currentSelectedPiece.tile.index === 239;
      const isFayette = this.currentSelectedPiece.tile.index === 283;
      const nextPieceTile = this.map.getTileAt(
        this.currentSelectedPiece.tile.x + direction[0],
        this.currentSelectedPiece.tile.y + direction[1],
        true,
        'logic_layer'
      );
      const clickedTileCanMoveTo =
        this.currentMoveSet &&
        (this.currentMoveSet.moveSet.includes(nextPieceTile) ||
          this.currentMoveSet.attackSet.includes(nextPieceTile));
      if (clickedTileCanMoveTo) {
        const modTileSize = this.map.tileWidth;
        const newX = nextPieceTile.x + modTileSize * 0.5;
        const newY = nextPieceTile.y + modTileSize * 0.5;
        this.cursorPointer.setPosition(newX, newY);
        // todo - handle for all pieces?
        if (isUncle || isBishop || isFayette) {
          this.game.movingPieceStandingTexture = swapPieceSpriteForMovementSprite(
            this,
            this.currentSelectedPiece,
            direction
          );
        }
        this.animateDroppedUnitMove(
          this.currentSelectedPiece,
          nextPieceTile,
          this.currentSelectedPiece.tile,
          true,
          isFayette
            ? (movedUnit, x, wasBounceback) => {
                setTimeout(
                  () => this.moveInDirectionAgain(movedUnit, x, wasBounceback),
                  FAYETTE_MOVEMENT_PROPERTIES.moveAgainDelay
                );
              }
            : this.moveInDirectionAgain
        );
      } else {
        this.game.directControlContinuousMovement = false;
        this.player &&
          this.player.movementDustEmitter &&
          this.player.movementDustEmitter.stop();
        this.isMoving = false;
        if (isUncle || isBishop) {
          this.currentSelectedPiece.setTexture('2020_units');
          this.currentSelectedPiece.setFrame(
            logicIndexToSpriteIndex(this.currentSelectedPiece.tile.index)
          );
        } else if (isFayette) {
          this.currentSelectedPiece.setAlpha(1);
        }
      }
    }
  };
  moveInDirectionAgain = (movedUnit, x, wasBounceback) => {
    if (!this.sys.settings.active || !this.pieces?.children?.entries) {
      this.game.directControlContinuousMovement = false;
      this.isMoving = false;
      return;
    }

    const sliders = [239, 273];
    const isBishop =
      this.currentSelectedPiece && this.currentSelectedPiece.tile.index === 273;
    const pieceIsSlider =
      this.currentSelectedPiece &&
      sliders.includes(this.currentSelectedPiece.tile.index);

    // analog stick directions
    const { name, coordinates: nextDirection } = getAnalogueDirection(this);
    let keyPressedStill = false;

    // keyboard/dpad direction
    let nextKBDpadDirection;
    if (this.game.rightPressed) {
      if ((this.game.upPressed || this.game.downPressed) && isBishop) {
        if (this.game.upPressed) {
          nextKBDpadDirection = [1, -1];
          if (this.game.lastDirectControlDirection === 'up/right') {
            keyPressedStill = true;
          }
          this.game.lastDirectControlDirection = 'up/right';
        } else if (this.game.downPressed) {
          nextKBDpadDirection = [1, 1];
          if (this.game.lastDirectControlDirection === 'down/right') {
            keyPressedStill = true;
          }
          this.game.lastDirectControlDirection = 'down/right';
        }
      } else {
        nextKBDpadDirection = [1, 0];
        if (this.game.lastDirectControlDirection === 'right') {
          keyPressedStill = true;
        }
        this.game.lastDirectControlDirection = 'right';
      }
    } else if (this.game.leftPressed) {
      if ((this.game.upPressed || this.game.downPressed) && isBishop) {
        if (this.game.upPressed) {
          nextKBDpadDirection = [-1, -1];
          if (this.game.lastDirectControlDirection === 'up/left') {
            keyPressedStill = true;
          }
          this.game.lastDirectControlDirection = 'up/left';
        } else if (this.game.downPressed) {
          nextKBDpadDirection = [-1, 1];
          if (this.game.lastDirectControlDirection === 'down/left') {
            keyPressedStill = true;
          }
          this.game.lastDirectControlDirection = 'down/left';
        }
      } else {
        nextKBDpadDirection = [-1, 0];
        if (this.game.lastDirectControlDirection === 'left') {
          keyPressedStill = true;
        }
        this.game.lastDirectControlDirection = 'left';
      }
    } else if (this.game.upPressed) {
      nextKBDpadDirection = [0, -1];
      if (this.game.lastDirectControlDirection === 'up') {
        keyPressedStill = true;
      }
      this.game.lastDirectControlDirection = 'up';
    } else if (this.game.downPressed) {
      nextKBDpadDirection = [0, 1];
      if (this.game.lastDirectControlDirection === 'down') {
        keyPressedStill = true;
      }
      this.game.lastDirectControlDirection = 'down';
    }

    if (name) {
      if (this.game.lastDirectControlDirection === name) {
        keyPressedStill = true;
      }
      this.game.lastDirectControlDirection = name;
    }
    if (keyPressedStill && pieceIsSlider && !wasBounceback) {
      this.game.directControlContinuousMovement = true;
    } else {
      this.game.directControlContinuousMovement = false;
      this.resetCurrentPieceMovementAnimation();
    }
    const isAllowedNow = this.isDirectInputAllowed();
    const direction = nextKBDpadDirection || nextDirection;
    if (
      isAllowedNow &&
      direction &&
      !(direction[0] === 0 && direction[1] === 0)
    ) {
      this.moveInDirection(direction);
    } else {
      this.isMoving = false;
      this.resetCurrentPieceMovementAnimation();
    }
  };
  resetCurrentPieceMovementAnimation = () => {
    const piece = this.currentSelectedPiece || this.previousSelectedPiece;
    const isBishop = piece && piece.tile.index === 273;
    const isUncle = piece && piece.tile.index === 239;
    this.player &&
      this.player.movementDustEmitter &&
      this.player.movementDustEmitter.stop();
    if (isUncle || isBishop) {
      piece.setTexture('2020_units');
      piece.setFrame(logicIndexToSpriteIndex(piece.tile.index));
    }
  };
  APressed = () => {
    const dialogue = this.scene.isActive('DialogueScene');
    const partyMenu = this.scene.get('PartyMenu');
    const partyMenuActive = this.scene.isActive('PartyMenu');
    // Selecting a Menu Option inside Party Menu
    if (partyMenuActive) {
      const consumed = partyMenu.APressed();
      if (consumed) {
        return;
      }
    }
    // Selecting a Menu Option in Game Over
    if (this.currentControllerMenuOptions) {
      return this.triggerSelectedMenuItem();
    }
    if (this.pieceInfoUI && this.pieceInfoUI.showing) {
      this.pieceInfoUI.hide();
      return;
    }
    // Moving the cursor and selecting something
    const touchedSprites = [];
    this.pieces.getChildren().forEach(piece => {
      if (
        piece.getBounds().contains(this.cursorPointer.x, this.cursorPointer.y)
      ) {
        touchedSprites.push(piece);
      }
    });
    if (
      this.cursorPointer &&
      this.cursorPointer.alpha &&
      this.cursorPointer.x > 0 &&
      this.cursorPointer.y > 0 &&
      !dialogue
    ) {
      this.genericPointerDown(this.cursorPointer, touchedSprites, true);
    }
  };
  NumPressed = key => {
    if (this.leaderSwitchAnimationActive) {
      return;
    }
    const partyMenu = this.scene.get('PartyMenu');
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (
      partyMenuActive &&
      partyMenu.partyButton &&
      partyMenu.partyButton.alpha &&
      !this.scene.isActive('DialogueScene') &&
      !this.inCutscene &&
      !this.isMoving &&
      !this.isMultiMoving
    ) {
      partyMenu.NumPressed(key);
    }
  };
  EPressed = () => {
    if (this.leaderSwitchAnimationActive) {
      return;
    }
    const partyMenu = this.scene.get('PartyMenu');
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (partyMenuActive) {
      partyMenu.EPressed();
    }
  };
  BPressed = () => {
    if (this.leaderSwitchAnimationActive) {
      return;
    }
    const partyMenu = this.scene.get('PartyMenu');
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (partyMenuActive) {
      partyMenu.BPressed();
      return;
    }
    if (this.pieceInfoUI && this.pieceInfoUI.showing) {
      this.pieceInfoUI.hide();
      return;
    }
    if (this.abilityButton && this.abilityButton.active) {
      this.abilityButton.setStateInactive();
      return;
    }
    if (this.currentSelectedPiece) {
      this.deselectCurrentPiece();
      return;
    }
    const gameMenuScene = this.scene.get('GameMenuScene');
    if (
      gameMenuScene &&
      gameMenuScene.undoButton.alpha &&
      !this.scene.isActive('DialogueScene') &&
      !this.inCutscene &&
      !this.isMoving &&
      !this.isMultiMoving
    ) {
      console.log('game B Pressed undo action...');
      gameMenuScene.undoButton.action();
    }
  };
  XPressed = () => {
    if (this.leaderSwitchAnimationActive) {
      return;
    }
    const partyMenu = this.scene.get('PartyMenu');
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (partyMenuActive) {
      partyMenu.XPressed();
    }
    if (this.abilityButton && this.abilityButton.XPressed) {
      this.abilityButton.XPressed();
    }
  };
  LBPressed = () => {
    const partyMenu = this.scene.get('PartyMenu');
    if (this.leaderSwitchAnimationActive) {
      partyMenu?.queueLeaderSwitchDirection?.(-1);
      return;
    }
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (
      partyMenuActive &&
      partyMenu.partyButton &&
      partyMenu.partyButton.alpha &&
      !this.scene.isActive('DialogueScene') &&
      !this.inCutscene &&
      !this.isMoving &&
      !this.isMultiMoving
    ) {
      return partyMenu.LBPressed();
    }
    if (
      this.battlingInWorld ||
      this.battlingInStory ||
      this.scene.key === 'CastleScene'
    ) {
      this.selectPrevNextBattleParticipant(-1);
    }
  };

  RBPressed = () => {
    const partyMenu = this.scene.get('PartyMenu');
    if (this.leaderSwitchAnimationActive) {
      partyMenu?.queueLeaderSwitchDirection?.(1);
      return;
    }
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (
      partyMenuActive &&
      partyMenu.partyButton &&
      partyMenu.partyButton.alpha &&
      !this.scene.isActive('DialogueScene') &&
      !this.inCutscene &&
      !this.isMoving &&
      !this.isMultiMoving
    ) {
      return partyMenu.RBPressed();
    }
    if (
      this.battlingInWorld ||
      this.battlingInStory ||
      this.scene.key === 'CastleScene'
    ) {
      this.selectPrevNextBattleParticipant(1);
    }
  };
  StartPressed = () => {
    if (this.leaderSwitchAnimationActive) {
      return;
    }
    if (this.sharedLevelMode) {
      return;
    }
    const menuScene = this.scene.get('OptionsMainMenuScene');
    if (
      menuScene &&
      this.scene.isActive('OptionsMainMenuScene') &&
      !this.scene.isActive('DialogueScene') &&
      !this.isMoving &&
      !this.inCutscene
    ) {
      menuScene.setMainMenuVisible(!menuScene.mainMenuVisible);
    }
  };

  EscapePressed = () => {
    if (this.leaderSwitchAnimationActive) {
      return;
    }
    if (this.sharedLevelMode) {
      return;
    }
    const menuScene = this.scene.get('OptionsMainMenuScene');
    if (
      menuScene &&
      this.scene.isActive('OptionsMainMenuScene') &&
      !this.scene.isActive('DialogueScene') &&
      !this.isMoving &&
      !this.inCutscene
    ) {
      menuScene.setMainMenuVisible(!menuScene.mainMenuVisible);
    }
  };

  addRandomEnemies(levelData, origin) {
    const lvlData = levelData || this.levelData.lvlData;
    MapHelpers.distributeRandomEnemies(this, lvlData, origin);
  }

  mousewheel = debounce((pointer, currentlyOver, dx, dy, dz, event) => {
    const partyMenu = this.scene.get('PartyMenu');
    const scrollDirection = dy > 0 ? -1 : 1;
    if (this.leaderSwitchAnimationActive) {
      partyMenu?.queueLeaderSwitchDirection?.(scrollDirection);
      return;
    }
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (
      partyMenuActive &&
      partyMenu.partyButton &&
      partyMenu.partyButton.alpha &&
      !this.scene.isActive('DialogueScene') &&
      !this.inCutscene &&
      !this.isMoving &&
      !this.isMultiMoving
    ) {
      partyMenu.bumperPressed(scrollDirection);
    }
  }, 100);

  mousemove = pointer => {
    this.game.canvas.style.cursor = this.getMouseCursorStyle(pointer);
    this.cursorPointer.setAlpha(0);
    this.game.controllerInput = false;
  };

  shouldUseCameraDragPan(pointer = this.input?.activePointer) {
    return (
      !!this.cameraDragPanning ||
      !!(
        this.paintMode &&
        this.hasEditingTools &&
        this.cursors?.space?.isDown &&
        pointer &&
        pointer.x >= SIDEBAR_WIDTH
      )
    );
  }

  getMouseCursorStyle(pointer = this.input?.activePointer) {
    if (this.shouldUseCameraDragPan(pointer)) {
      return cursorHandCSS;
    }

    return cursorCSS[this.game.defaultZoom];
  }

  selectPrevNextBattleParticipant = direction => {
    const bluePieces = this.pieces
      .getChildren()
      .filter(piece => piece.color === 'blue');
    const currentSelectedPieceIndex = this.currentSelectedPiece
      ? bluePieces.findIndex(piece => piece.id === this.currentSelectedPiece.id)
      : -1;
    let nextPieceSprite;
    const nextIndex = currentSelectedPieceIndex + direction;
    if (nextIndex < bluePieces.length && nextIndex > -1) {
      nextPieceSprite = bluePieces[currentSelectedPieceIndex + direction];
    } else {
      nextPieceSprite = bluePieces[direction === 1 ? 0 : bluePieces.length - 1];
    }
    this.hideAbilityButton();
    this.pickupUnit(nextPieceSprite, true);
    if (this.scene.key === 'CastleScene') {
      this.focusOnRoomWithPiece(nextPieceSprite);
    }
    if (this.pieceInfoUI && this.pieceInfoUI.showing) {
      this.pieceInfoUI.hide();
    }
  };

  genericPointerDown(pointer, gameObject, controllerActivated) {
    let pointerTileX, pointerTileY;
    if (this.hideRequirementsPopup && this.requirementsPopupVisible) {
      this.hideRequirementsPopup();
      this.pickupUnit(this.player);
    }
    if (controllerActivated) {
      pointerTileX = this.map.worldToTileX(this.cursorPointer.x);
      pointerTileY = this.map.worldToTileY(this.cursorPointer.y);
    } else {
      var worldPoint = pointer.positionToCamera(this.cameras.main);
      pointerTileX = this.map.worldToTileX(worldPoint.x);
      pointerTileY = this.map.worldToTileY(worldPoint.y);
    }
    recordPointerTelemetry(this, pointer, {
      action: controllerActivated
        ? 'game_controller_select'
        : 'game_pointer_select',
      input: controllerActivated ? 'controller' : undefined,
      tile_x: pointerTileX,
      tile_y: pointerTileY
    });
    // console.log(
    //   'clicked tile:',
    //   gameObject,
    //   this.currentSelectedPiece,
    //   {
    //     worldXY: worldPoint && { x: worldPoint.x, y: worldPoint.y },
    //     tile: {
    //       x: pointerTileX,
    //       y: pointerTileY
    //     }
    //   },
    //   this.disableInputCommandChain,
    //   this.disableInput,
    //   this.inCutscene,
    //   this.gameOverContainer,
    //   this.gameOverContainer && this.gameOverContainer.alpha
    // );
    //deslect text input in Level Editor during generic click
    const LEInput = document.querySelector('.name-input input');
    if (LEInput) {
      LEInput.blur();
    }
    if (
      this.disableInput ||
      this.disableInputCommandChain ||
      (this.gameOverContainer && this.gameOverContainer.alpha) ||
      this.inCutscene
    ) {
      return;
    }

    //if undo menu open, dont click through to tiles...

    let nextPieceTile = this.map.getTileAt(
      pointerTileX,
      pointerTileY,
      true,
      'logic_layer'
    );
    // handle projectile items selecting a target
    const clickedOrigin =
      this.currentSelectedPiece &&
      nextPieceTile &&
      nextPieceTile.x === this.currentSelectedPiece.tile.x &&
      nextPieceTile.y === this.currentSelectedPiece.tile.y;
    if (this.projectileAiming && this.currentSelectedPiece && !clickedOrigin) {
      const clikedTargetableTile = (this.projectileTargets || []).find(
        targetTile => {
          return (
            nextPieceTile.x === targetTile.x && nextPieceTile.y === targetTile.y
          );
        }
      );
      const item = ALL_ITEMS[this.currentSelectedPiece.itemID];
      if (clikedTargetableTile && item && item.fire) {
        return this.itemTurn(
          item,
          this.currentSelectedPiece.tile,
          nextPieceTile
        );
      } else {
        if (item && item.preMoveButton && item.preMoveButton.cancel) {
          item.preMoveButton.cancel(this);
        }
      }
    } else if (!clickedOrigin) {
      this.projectileAiming = false;
      this.projectileTargets = [];
    }

    let clickedSprite = gameObject[0];
    if (gameObject && controllerActivated) {
      clickedSprite = gameObject.find(clickedSprite => {
        const clickedSpriteTile = clickedSprite.tile;
        if (clickedSpriteTile) {
          if (
            clickedSpriteTile.x === nextPieceTile.x &&
            clickedSpriteTile.y === nextPieceTile.y
          ) {
            return true;
          }
        }
        return false;
      });
    }
    if (this.currentSelectedPiece) {
      if (clickedSprite) {
        if (!clickedSprite.tile) {
          //clicked something else
          return;
        }
        let clickedPieceCanBeKilled =
          this.currentMoveSet &&
          clickedSprite.color === 'red' &&
          (this.currentMoveSet.moveSet.includes(nextPieceTile) ||
            this.currentMoveSet.attackSet.includes(nextPieceTile));
        const clickedSpriteIsAnItem = ITEMS.includes(nextPieceTile.index);
        const clickedSelf =
          clickedSprite.tile.x === this.currentSelectedPiece.tile.x &&
          this.currentSelectedPiece.tile.y === clickedSprite.tile.y;
        if (clickedSelf) {
          if (this.pieceInfoUI && !this.pieceInfoUI.showing) {
            this.pieceInfoUI.showPiece(pointer, this.currentSelectedPiece);
          } else if (this.pieceInfoUI) {
            this.pieceInfoUI.hide();
          }
        } else {
          this.hideAbilityButton();
          if (clickedPieceCanBeKilled || clickedSpriteIsAnItem) {
            this.dropUnit(
              pointer,
              this.currentSelectedPiece,
              controllerActivated
            );
            if (this.scene.key === 'CastleScene' && !clickedSpriteIsAnItem) {
              this.focusOnRoomWithPiece(clickedSprite);
            }
          } else {
            this.pickupUnit(clickedSprite, controllerActivated);
            if (this.scene.key === 'CastleScene') {
              this.focusOnRoomWithPiece(clickedSprite);
            }
          }
          if (this.pieceInfoUI && this.pieceInfoUI.showing) {
            this.pieceInfoUI.hide();
          }
        }
      } else {
        this.dropUnit(pointer, this.currentSelectedPiece, controllerActivated);
        let clickedTileCanMoveTo =
          this.currentMoveSet &&
          (this.currentMoveSet.moveSet.includes(nextPieceTile) ||
            this.currentMoveSet.attackSet.includes(nextPieceTile));
        if (!clickedTileCanMoveTo) {
          if (this.scene.key === 'CastleScene') {
            this.focusOnActiveRooms();
          }
        }
        if (this.pieceInfoUI && this.pieceInfoUI.showing) {
          this.pieceInfoUI.hide();
        }
      }
    } else if (clickedSprite) {
      this.pickupUnit(clickedSprite, controllerActivated);
      if (this.scene.key === 'CastleScene') {
        this.focusOnRoomWithPiece(clickedSprite);
      }
      if (this.pieceInfoUI && this.pieceInfoUI.showing) {
        this.pieceInfoUI.hide();
      }
    }
  }
  getZoomLevel = customRect => {
    let layer = this.map.layers[0] || {};
    if (customRect) {
      layer.widthInPixels = customRect.width * 32;
      layer.heightInPixels = customRect.height * 32;
    }

    //show full map
    const verticalZoomLevel = window.innerWidth / (layer.widthInPixels + 32); // + 196;
    const horizontalZoomLevel =
      window.innerHeight / (layer.heightInPixels + 32); // + 196;
    let minZoom = Math.min(horizontalZoomLevel, verticalZoomLevel);
    let closestDistance = 4;
    let closestZoomLevel = 1;
    ZOOM_LEVELS.forEach(level => {
      let distance = Math.abs(level - minZoom);
      if (distance < closestDistance) {
        closestDistance = distance;
        closestZoomLevel = level;
      }
    });
    return closestZoomLevel;
  };
  setZoomLevel(customRect) {
    const zoom = this.getZoomLevel(customRect);
    this.zoomLevel = zoom;
    this.cameras.main.setZoom(zoom);
  }
  showHitPointsUI(tile) {
    const x = tile.pixelX + 22;
    const y = tile.pixelY;
    this.game.soundManager.playSfx('amuletUsage');
    setTimeout(() => {
      this.amuletCrystal.setPosition(x + 26, y + 32);
      this.amuletCrystal.setDepth(tile.occupant.parentContainer.depth + 1);
      this.amuletCrystal.setAlpha(1);
      this.amuletCrystal.anims.play('amulet-once-thru');
      this.amuletCrystal.once('animationcomplete', () => {
        const color = tile.occupant.color === 'red' ? 'blue' : 'red';
        History.saveMapState(this, color);
        this.checkNextTurn({ occupant: { color } });
      });
    }, 250);
  }
  dimPieceAbility = piece => {
    if (!piece || !piece.parentContainer) {
      return;
    }
    const itemContainer = piece.parentContainer.list.find(
      gameObject => gameObject.type === 'Container'
    );
    if (!itemContainer) {
      return;
    }
    const itemIcon = itemContainer.getAt(0);
    itemIcon.setAlpha(0.6);
  };
  getTilePixelPosition(tile) {
    const tileWidth = tile.width || this.map.baseTileWidth || 32;
    const tileHeight = tile.height || this.map.baseTileHeight || 32;
    return {
      x: typeof tile.pixelX === 'number' ? tile.pixelX : tile.x * tileWidth,
      y: typeof tile.pixelY === 'number' ? tile.pixelY : tile.y * tileHeight
    };
  }
  getLogicTile(tile) {
    if (!tile || typeof tile.x !== 'number' || typeof tile.y !== 'number') {
      return tile;
    }
    const logicTile = this.map.getTileAt(tile.x, tile.y, true, 'logic_layer');
    return logicTile &&
      typeof logicTile.x === 'number' &&
      typeof logicTile.y === 'number'
      ? logicTile
      : tile;
  }
  updatePieceSprites(mover = null, destination = null) {
    if ((isInFreeRoam(this) || this.sharedLevelMode) && mover) {
      this.dimPieceAbility(mover);
      return mover;
    }
    let returnedPiece;
    let moverCoords;
    if (mover) {
      moverCoords = {
        x: mover.tile.x,
        y: mover.tile.y
      };
    }
    let currentCoords;
    if (this.currentSelectedPiece && this.currentSelectedPiece.tile) {
      currentCoords = {
        x: this.currentSelectedPiece.tile.x,
        y: this.currentSelectedPiece.tile.y
      };
    }
    let prevCoords;
    if (this.previousSelectedPiece && this.previousSelectedPiece.tile) {
      prevCoords = {
        x: this.previousSelectedPiece.tile.x,
        y: this.previousSelectedPiece.tile.y
      };
    }
    let pieces = this.pieces.getChildren().slice(0);
    pieces.sort((a, b) => {
      let ayValue = a.y;
      let byValue = b.y;
      if (moverCoords && destination) {
        if (a.tile.x === moverCoords.x && a.tile.y === moverCoords.y) {
          ayValue = destination.pixelY + 34;
        }
        if (b.tile.x === moverCoords.x && b.tile.y === moverCoords.y) {
          byValue = destination.pixelY + 34;
        }
      }
      return ayValue - byValue;
    });

    const worldPlayerCoords =
      this.player && this.player.tile
        ? { x: this.player.tile.x, y: this.player.tile.y }
        : null;

    pieces.forEach(piece => {
      const container = piece.parentContainer;
      container.removeAll(true);
      container.destroy();
    });

    pieces = pieces.map(piece => {
      let pieceIndex = (piece.tile && piece.tile.index) || piece.frame.name;
      if (!this.battlingInWorld && pieceIndex === 616) {
        pieceIndex = this.levelData.lvlData.logic[piece.tile.y][piece.tile.x];
      }
      return {
        pieceIndex,
        tile: piece.tile,
        hp: piece.hp,
        id: piece.id,
        itemID: piece.itemID,
        item: ALL_ITEMS[piece.itemID],
        itemActive: piece.itemActive,
        itemCharges: piece.itemCharges,
        color: piece.color
      };
    });
    this.pieces.clear(true, true);
    pieces.forEach(pieceData => {
      let piece = addPieceSpriteToBoard(
        this,
        pieceData.tile,
        ALL_ITEMS[pieceData.itemID],
        pieceData.pieceIndex
      );
      checkAndApplyIdleAnimation(this, piece, pieceData);
      piece.id = pieceData.id;
      if (pieceData.hp) {
        piece.hp = pieceData.hp;
        if (
          pieceData.item &&
          pieceData.item.type === 'hitpoints' &&
          piece.hp === 1
        ) {
          this.dimPieceAbility(piece);
        }
      }
      piece.itemActive = pieceData.itemActive;
      if (pieceData.itemCharges || pieceData.itemCharges === 0) {
        piece.itemCharges = pieceData.itemCharges;
        if (pieceData.itemCharges < 1) {
          this.dimPieceAbility(piece);
        }
      }
      const worldPlayer =
        worldPlayerCoords &&
        piece.tile.x === worldPlayerCoords.x &&
        piece.tile.y === worldPlayerCoords.y;
      if (this.sys.config === 'WorldScene' && worldPlayer) {
        this.player = piece;
      }
      if (
        currentCoords &&
        pieceData.tile.x === currentCoords.x &&
        pieceData.tile.y === currentCoords.y
      ) {
        this.currentSelectedPiece = piece;
      }
      if (
        prevCoords &&
        pieceData.tile.x === prevCoords.x &&
        pieceData.tile.y === prevCoords.y
      ) {
        this.previousSelectedPiece = piece;
      }
      if (
        moverCoords &&
        pieceData.tile.x === moverCoords.x &&
        pieceData.tile.y === moverCoords.y
      ) {
        returnedPiece = piece;
      }
    });
    return returnedPiece;
  }
  animateMove = (
    unit,
    pieceTile,
    origin,
    directControlMovement = false,
    skipNext = null,
    cb = null
  ) => {
    pieceTile = this.getLogicTile(pieceTile);
    origin = this.getLogicTile(origin);
    let moverUnit = this.updatePieceSprites(unit, pieceTile);
    this.disableInput = true;
    const hitRed = RED_PIECES.includes(pieceTile.index);
    const hitBlue = BLUE_PIECES.includes(pieceTile.index);
    const hitColorless = COLORLESS_PIECES.includes(pieceTile.index);
    const madeKill = hitRed || hitBlue || hitColorless;
    const groundIndex = this.map.getTileAt(
      pieceTile.x,
      pieceTile.y,
      true,
      'ground_layer'
    ).index;
    let bounceback = false;
    let direction = [0, 0];
    const npc = pieceTile.index === WALKABLE;
    const pieceHasMoreThan1Hp =
      pieceTile.occupant &&
      pieceTile.occupant.hp &&
      pieceTile.occupant.hp > 1 &&
      !(pieceTile.occupant === unit);
    if (npc || pieceHasMoreThan1Hp) {
      bounceback = true;
      direction = getDirection(origin, pieceTile);
    }
    if (madeKill && !bounceback) {
      this.game.soundManager.playSfx('killPiece');
    } else {
      this.game.soundManager.playRandomStepForGroundIndex(groundIndex);
    }

    if (origin.y === pieceTile.y - 1 && origin.x === pieceTile.x) {
      this.map.removeTileAt(origin.x, origin.y, false, false, 'shade_layer');
    }

    // above doors covering
    let belowPlayerTile = this.map.getTileAt(
      origin.x,
      origin.y,
      true,
      'below_player'
    );
    let southOfDestinationTile = this.map.getTileAt(
      pieceTile.x,
      pieceTile.y + 1,
      true,
      'below_player'
    );
    if (
      southOfDestinationTile &&
      DOORS.includes(southOfDestinationTile.index) &&
      belowPlayerTile &&
      southOfDestinationTile !== belowPlayerTile
    ) {
      this.map.putTileAt(
        southOfDestinationTile.index - 18,
        southOfDestinationTile.x,
        southOfDestinationTile.y - 1,
        true,
        'shade_layer'
      );
    }

    const isFayette = unit.tile.index === 283;
    let moveDuration =
      isFayette && directControlMovement
        ? FAYETTE_MOVEMENT_PROPERTIES.moveDuration
        : this.game.defaultMovementDuration; // default
    let ease = madeKill ? 'Back.easeIn' : 'Quad.easeIn';
    if (directControlMovement) {
      if (!isFayette) {
        ease = madeKill ? 'Back.easeIn' : 'Linear';
      }
      if (this.game.directControlContinuousMovement) {
        this.game.currentMoveDuration =
          this.game.currentMoveDuration - this.game.movementAcceleration;
        if (this.game.currentMoveDuration < this.game.minimumMoveDuration) {
          this.game.currentMoveDuration = this.game.minimumMoveDuration;
        }
        moveDuration = this.game.currentMoveDuration;
      } else {
        this.game.currentMoveDuration = this.game.defaultMovementDuration;
      }
    } else {
      this.game.currentMoveDuration = this.game.defaultMovementDuration;
    }
    if (this.game.currentMoveDuration <= 200) {
      if (this.player && this.player.movementDustEmitter) {
        this.player.movementDustEmitter.setPosition(
          moverUnit.x - 12,
          moverUnit.y + 24
        );
        const moveDirection = getDirection(origin, pieceTile);
        const movingDown = moveDirection[1] === 1;
        if (movingDown) {
          this.player.parentContainer.sendToBack(
            this.player.movementDustEmitter.manager
          );
        } else {
          this.player.parentContainer.bringToTop(
            this.player.movementDustEmitter.manager
          );
        }
        const newAngle =
          DUST_EMITTER_ANGLES[moveDirection[0]][moveDirection[1]];
        this.player.movementDustEmitter.setAngle(newAngle);
        if (!this.player.movementDustEmitter.on) {
          this.player.movementDustEmitter.start();
        }
      }
    }
    if (pieceHasMoreThan1Hp) {
      this.showHitPointsUI(pieceTile);
    }
    const destinationPixels = this.getTilePixelPosition(pieceTile);
    const tweenTarget = {
      x: destinationPixels.x + 48 + direction[0] * -1 * 16,
      y:
        destinationPixels.y +
        32 +
        direction[1] * -1 * 16 -
        (pieceTile.tileHeightOffset || 0)
    };
    this.tweens.add({
      targets: moverUnit.parentContainer,
      x: tweenTarget.x,
      y: tweenTarget.y,
      ease,
      delay: isFayette ? FAYETTE_MOVEMENT_PROPERTIES.moveDelay : 0,
      duration: moveDuration,
      onComplete: () => {
        this.shadow1 && this.shadow1.setAlpha(0);
        this.animateDeath(madeKill, hitRed, pieceTile, bounceback, () => {
          if (bounceback) {
            pieceTile.occupant && pieceTile.occupant.setAlpha(1);
            this.bouncebackMoveUnit(
              moverUnit,
              pieceTile,
              origin,
              skipNext || pieceHasMoreThan1Hp
            );
          } else {
            this.moveUnitToTile(
              moverUnit,
              pieceTile,
              origin,
              skipNext || pieceHasMoreThan1Hp
            );
          }
          if (cb) {
            cb(moverUnit, pieceTile, bounceback);
          }
        });
      }
    });
  };

  animateDeath(madeKill, hitRed, pieceTile, bounceback, cb) {
    let callbackResolved = false;
    let fallbackTimer = null;
    const resolveAnimation = () => {
      if (callbackResolved) return;
      callbackResolved = true;
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
      }
      setTimeout(cb, 0);
    };
    const particleDeath = particle => {
      const emitter = this.redParticleEmitter;
      if (!emitter) {
        resolveAnimation();
        return;
      }
      const count = emitter.getParticleCount();
      const dead = emitter.getDeadParticleCount();

      if (count === dead) {
        resolveAnimation();
      }
    };
    if (madeKill && !bounceback) {
      this.cameras.main.shake(150, 0.0015);
      pieceTile.occupant.setAlpha(0);
      if (bounceback) {
        // this.showHitPointsUI(pieceTile);
      }
      const pieceTilePixels = this.getTilePixelPosition(pieceTile);
      let x = pieceTilePixels.x + 48;
      let y = pieceTilePixels.y + 32;
      const small = SMALL_PIECES.includes(pieceTile.index);
      const frames = hitRed ? [0, 1, 2, 3, 4, 5, 6] : [7, 8, 9, 10, 11, 12, 13];
      const pieceHitBox = new Phaser.Geom.Rectangle(-4, 0, 8, 24);
      if (this.pieceParticles) {
        this.redParticleEmitter = this.pieceParticles.createEmitter({
          frame: {
            frames,
            cycle: true,
            quantity: 1
          },
          x,
          y,
          angle: { min: 0, max: 180 },
          gravityY: 80,
          quantity: bounceback ? 4 : small ? 7 : 12,
          speed: { start: 50, end: 0, ease: 'Bounce.easeOut' },
          lifespan: { min: 550, max: 1250 },
          frequency: 1000,
          emitZone: { type: 'random', source: pieceHitBox },
          bounce: 0.8,
          bounds: {
            x: x - 48,
            y: y - 48,
            width: 128,
            height: 64
          },
          collideBottom: true,
          collideTop: false,
          collideLeft: false,
          collideRight: false
        });
        this.redParticleEmitter.explode();
        this.redParticleEmitter.onParticleDeath(particleDeath);
        fallbackTimer = setTimeout(resolveAnimation, 1400);
      } else {
        resolveAnimation();
      }
    } else if (madeKill) {
      this.cameras.main.shake(100, 0.001);
      resolveAnimation();
    } else {
      resolveAnimation();
    }
  }

  equipItem = async (piece, targetTile, item) =>
    new Promise(resolve => {
      // todo we may need to await item.init in the future
      equipWeaponToPiece(item, piece);
      if (item.init) {
        item.init(this, targetTile, piece);
      }
      resolve(true);
    });

  async handleItem(piece, targetTile) {
    const allItemsID = itemIDsBySpriteIndex[targetTile.index];
    if (allItemsID) {
      this.numGroundItemsPickedUp += 1;
      await this.equipItem(piece, targetTile, ALL_ITEMS[allItemsID]);
      return;
    }
    let pickedUpGold = false;
    switch (targetTile.index) {
      case MAP:
        this.game.registry.set('hasMap', true);
        if (this.minimap) {
          this.minimap.setVisible(true);
        }
        break;
      case KEY:
        this.game.registry.set('hasKey', true);
        this.game.soundManager.playSfx('key');
        const menuScene = this.scene.get('GameMenuScene');
        if (menuScene) {
          menuScene.showKeyItem();
        }
        break;
      case GOLD_TILES[0]:
        this.game.player.gold = addGoldToPlayer(1);
        pickedUpGold = true;
        break;
      case GOLD_TILES[1]:
        this.game.player.gold = addGoldToPlayer(3);
        pickedUpGold = true;
        break;
      case GOLD_TILES[2]:
        this.game.player.gold = addGoldToPlayer(10);
        pickedUpGold = true;
        break;
      case GOLD_TILES[3]:
        this.game.player.gold = addGoldToPlayer(25);
        pickedUpGold = true;
        break;
      case LOCKED_DOOR:
        // lockedDoor
        this.game.sfx.unlock.play({ volume: 0.3 });
        break;
      default:
        break;
    }
    // save progress of picking up gold in world
    const inWorld = this.scene.key === 'WorldScene';
    if (inWorld && pickedUpGold) {
      saveWorldProgress(`g${targetTile.x}_${targetTile.y}`);
    }
    if (pickedUpGold) {
      this.game.soundManager.playSfx('coins');
    }
    return;
  }
  getLostMainCharacter() {
    return this.captured.blue.find(
      piece => this.pieceMap[piece.id] && this.pieceMap[piece.id].main
    );
  }
  endGame = (condition, win, lostMainCharacter) => {
    if (!win && lostMainCharacter) {
      return this.gameOver();
    }
    let message = win ? 'Level Complete' : 'Failure';
    message += `\n${condition}`;

    const isLevelEditor = this.scene.key === 'LevelEditorScene';

    const buttons = [];

    if (isLevelEditor) {
      if (win) {
        if (typeof this.getLevelEditorWinButtons === 'function') {
          buttons.push(...this.getLevelEditorWinButtons());
        } else {
          buttons.push({
            label: 'No, Keep Editing',
            action: this.restartBattle
          });
        }
      }
    } else if (!this.sharedLevelMode) {
      buttons.push({
        label: 'Main Menu',
        action: this.gotoMainMenu
      });
    }

    if (this.freeplayHighscore && !this.sharedLevelMode) {
      buttons.push({
        label: 'Return to Freeplay',
        action: () => {
          const menuScene = this.scene.get('OptionsMainMenuScene');
          menuScene && menuScene.returnToFreeplay();
        }
      });
    }

    if (!win) {
      buttons.push({
        label: 'Restart Battle',
        action: this.restartBattle
      });
    }

    this.scene.launch('DialogueScene', {
      scene: this,
      text: message,
      end: () => {
        this.scene.stop('DialogueScene');
        this.inCutscene = false;
        this.projectileAiming = false;
      },
      options: buttons
    });
  };

  gameOver = () => {
    const cam = this.cameras.main;
    if (cam.zoom !== this.game.defaultZoom) {
      cam.zoomTo(
        this.game.defaultZoom,
        400,
        'Quad.easeInOut',
        true,
        (camera, progress) => {
          if (progress === 1) {
            this.showGameOverScreen();
          }
        }
      );
    } else {
      this.showGameOverScreen();
    }
  };

  restoreGameplayCameraAfterGameOver = cb => {
    if (this.focusOnActiveRooms) {
      this.focusOnActiveRooms(cb);
      return;
    }

    this.setZoomLevel();
    if (this.stageCenter) {
      this.cameras.main.centerOn(this.stageCenter.x, this.stageCenter.y);
    }
    cb && cb();
  };

  showGameOverScreen = () => {
    const menuScene = this.scene.get('GameMenuScene');
    if (menuScene && menuScene.undoButton) {
      menuScene.undoButton.alpha = 0;
    }
    this.game.soundManager.transitionToTrack(
      this,
      'music_game_over',
      'bgMusic'
    );
    this.currentControllerMenuOptions = this.sharedLevelMode
      ? createSharedGameOverControllerMenuOptions()
      : gameOverControllerMenuOptions;
    const cam = this.cameras.main;
    this.disableInput = true;
    this.gameOverContainer = this.add.container(
      cam.worldView.left,
      cam.worldView.top
    );
    this.gameOverContainer.setAlpha(0.01).setDepth(25);
    this.gameOverBackdropRect = new Phaser.Geom.Rectangle(
      0,
      0,
      this.game.canvas.width,
      this.game.canvas.height
    );
    this.gameOverBackdrop = this.add.graphics();
    this.gameOverBackdrop.fillStyle(0x1f272c, 0.75);
    this.gameOverBackdrop.fillRectShape(this.gameOverBackdropRect);
    this.gameOverBackdrop.setInteractive(
      this.gameOverBackdropRect,
      Phaser.Geom.Rectangle.Contains
    );
    this.gameOverBackdrop.on('pointerdown', (pointer, x, y, evt) => {
      //todo
      evt.stopPropagation();
    });

    const zoom = this.game.defaultZoom;

    this.gameOverBackground = this.add
      .sprite(cam.centerX / zoom, cam.centerY / zoom, 'ui_gameover_bg')
      .setOrigin(0.5);

    this.gameOverUndo = SpriteButton(this, {
      calcX: scene => cam.centerX / zoom,
      calcY: scene => cam.centerY / zoom - 60,
      sprite: 'ui_gameover_undo',
      dontScale: true,
      action: () => {
        setTimeout(() => {
          this.restartBattleMusic();
          this.clearCurrentMenuOptions();
          const gameMenuScene = this.scene.get('GameMenuScene');
          gameMenuScene.undoLastMove(() => {
            this.gameOverContainer.setAlpha(0);
            this.restoreGameplayCameraAfterGameOver(() => {
              this.disableInput = false;
            });
          });
        }, 100);
      }
    })
      .setOrigin(0.5)
      .setScale(1);
    this.currentControllerMenuOptions.undo.button = this.gameOverUndo;

    this.gameOverRetry = SpriteButton(this, {
      calcX: scene => cam.centerX / zoom,
      calcY: scene => cam.centerY / zoom - 45,
      sprite: 'ui_gameover_retry',
      dontScale: true,
      action: () => {
        setTimeout(() => {
          this.clearCurrentMenuOptions();
          this.restartBattle();
          this.gameOverContainer.setAlpha(0);
          this.disableInput = false;
        }, 0);
      }
    })
      .setOrigin(0.5)
      .setScale(1);
    this.currentControllerMenuOptions.retry.button = this.gameOverRetry;

    if (!this.sharedLevelMode && this.freeplayHighscore) {
      this.gameOverFreeplay = SpriteButton(this, {
        calcX: scene => cam.centerX / zoom,
        calcY: scene => cam.centerY / zoom - 30,
        sprite: 'ui_gameover_freeplay',
        dontScale: true,
        action: () => {
          this.removePlantedTrees();
          this.clearCurrentMenuOptions();
          const menuScene = this.scene.get('OptionsMainMenuScene');
          this.gameOverContainer.setAlpha(0);
          menuScene && menuScene.returnToFreeplay();
        }
      })
        .setOrigin(0.5)
        .setScale(1);
      this.currentControllerMenuOptions.overworld_freeplay.button = this.gameOverFreeplay;
    } else if (!this.sharedLevelMode) {
      this.gameOverOverworld = SpriteButton(this, {
        calcX: scene => cam.centerX / zoom,
        calcY: scene => cam.centerY / zoom - 30,
        sprite: 'ui_gameover_overworld',
        dontScale: true,
        action: () => {
          this.removePlantedTrees();
          this.clearCurrentMenuOptions();
          const menuScene = this.scene.get('OptionsMainMenuScene');
          menuScene && menuScene.returnToOverworld({ fromGameOver: true });
          this.gameOverContainer.setAlpha(0);
        }
      })
        .setOrigin(0.5)
        .setScale(1);
      this.currentControllerMenuOptions.overworld_freeplay.button = this.gameOverOverworld;
    }

    if (!this.sharedLevelMode) {
      this.gameOverQuit = SpriteButton(this, {
        calcX: scene => cam.centerX / zoom,
        calcY: scene => cam.centerY / zoom - 15,
        sprite: 'ui_gameover_quit',
        dontScale: true,
        action: () => {
          this.gameOverContainer.setAlpha(0);
          this.clearCurrentMenuOptions();
          this.gotoMainMenu();
        }
      })
        .setOrigin(0.5)
        .setScale(1);
      this.currentControllerMenuOptions.main_menu.button = this.gameOverQuit;
    }

    this.gameOverContainer.add(this.gameOverBackdrop);
    this.gameOverContainer.add(this.gameOverBackground);
    this.gameOverContainer.add(this.gameOverRetry);
    this.gameOverContainer.add(this.gameOverUndo);
    if (!this.sharedLevelMode) {
      this.gameOverContainer.add(
        this.freeplayHighscore ? this.gameOverFreeplay : this.gameOverOverworld
      );
      this.gameOverContainer.add(this.gameOverQuit);
    }

    this.add.tween({
      targets: this.gameOverContainer,
      alpha: 1,
      ease: 'Expo.easeIn',
      duration: 1200
    });

    this.highlightButtonByName('undo');
  };

  highlightButtonByName = (name, dir) => {
    const menuOption = this.currentControllerMenuOptions[name];
    if (menuOption && !menuOption.disabled) {
      this.currentMenuSelection = menuOption;
      menuOption.selected = true;
      if (menuOption.button && menuOption.button.setFrame) {
        menuOption.button.setFrame(1);
      } else if (menuOption.button && menuOption.button.setSelected) {
        menuOption.button.setSelected(true);
      }
      menuOption.button && this.game.sfx.pointerover.play();
    } else if (menuOption) {
      const nextName = menuOption.directions[dir];
      this.highlightButtonByName(nextName, dir);
    }
  };
  resetMenuButtons() {
    Object.keys(this.currentControllerMenuOptions).forEach(key => {
      const option = this.currentControllerMenuOptions[key];
      option.selected = false;
      if (option.button && option.button.setFrame) {
        option.button.setFrame(option.disabled ? 3 : 0);
      } else if (option.button && option.button.setSelected) {
        option.button.setSelected(false);
      }
    });
  }
  clearCurrentMenuOptions = () => {
    this.currentControllerMenuOptions = null;
    this.currentMenuSelection = null;
  };
  moveInDirectionGameOverMenu = direction => {
    let dir = 'down';

    if (direction[1] === -1) {
      dir = 'up';
    } else if (direction[1] === 0) {
      dir = '';
    } else if (direction[1] === 1) {
      dir = 'down';
    }
    if (direction[0] === -1) {
      dir += 'left';
    } else if (direction[0] === 0) {
      dir += '';
    } else if (direction[0] === 1) {
      dir += 'right';
    }
    const nextButtonName =
      this.currentMenuSelection && this.currentMenuSelection.directions[dir];
    if (
      this.currentMenuSelection &&
      this.currentMenuSelection.directions[dir]
    ) {
      this.resetMenuButtons();
      this.highlightButtonByName(nextButtonName, dir);
    }
  };
  triggerSelectedMenuItem = () => {
    const action =
      this.currentMenuSelection &&
      this.currentMenuSelection.button &&
      this.currentMenuSelection.button.action;

    if (action) {
      this.game.sfx.click.play();
      action();
    }
  };

  gotoMainMenu = () => {
    endSceneAndChildScenes(this, () => {
      this.scene.start('MenuScene');
    });
  };

  returnToFreeplayFromResults = () => {
    const menuScene = this.scene.get('OptionsMainMenuScene');
    if (menuScene && menuScene.returnToFreeplay) {
      menuScene.returnToFreeplay();
    } else {
      endSceneAndChildScenes(this, () => {
        this.scene.start('FreeplayShowcase');
      });
    }
  };

  removePlantedTrees = () => {
    if (this.plantedTrees?.length) {
      this.plantedTrees.forEach(t => {
        this.map.removeTileAt(t.x, t.y, false, true, 'below_player');
        this.map.removeTileAt(t.x, t.y - 1, false, true, 'above_player');
        this.map.removeTileAt(t.x, t.y, false, true, 'logic_layer');
        if (t.below_player) {
          this.map.putTileAt(t.below_player, t.x, t.y, true, 'below_player');
        }
        if (t.above_player) {
          this.map.putTileAt(
            t.above_player,
            t.x,
            t.y - 1,
            true,
            'above_player'
          );
        }
        if (t.logic_layer) {
          this.map.putTileAt(t.logic_layer, t.x, t.y, true, 'logic_layer');
        }
      });
      this.plantedTrees.length = 0;
    }
  };
  restartBattleMusic = () => {
    let trackName = 'music_battle';
    if (
      this.inBattleLevelData &&
      this.currentBiome &&
      this.currentBiome.battleMusic
    ) {
      trackName = this.currentBiome.battleMusic;
    }
    if (this.levelData.startingMusic) {
      trackName = this.levelData.startingMusic;
    }
    if (this.inBattleLevelData && this.inBattleLevelData.startingMusic) {
      trackName = this.inBattleLevelData.startingMusic;
    }
    this.game.soundManager.transitionToTrack(this, trackName, 'bgMusic');
  };
  restartBattle = () => {
    this.removePlantedTrees();
    this.restartBattleMusic();
    this.clearAuthoredPuzzleSwitches();

    Movement.clearTileHighlighting(this.map, this.inZone && this.inZone.rect);
    this.scene.stop('DialogueScene');

    for (let i = 1; i < 5; i++) {
      // console.log('restart applying to layer:', i);
      History.applyMapState(
        this,
        this.map,
        0,
        null,
        this.scene.key !== 'CastleScene',
        true,
        this.game.registry.get('history_initial_piece_map'),
        i
      );
    }

    History.resetHistory(this);
    this.setupAuthoredPuzzleSwitches();
    History.saveMapState(this, 'blue');
    this.abilityButton && this.abilityButton.setAlpha(0);
    this.captured = { blue: [], red: [] };
    this.numUndosUsed = 0;
    this.numGroundItemsPickedUp = 0;
    this.timeLevelStarted = new Date().getTime();
    this.updatePieceSprites();
    const menuScene = this.scene.get('GameMenuScene');
    if (this.levelData && this.levelData.onRestart) {
      this.levelData.onRestart(this);
    }
    if (this.battlingInWorld && this.inBattleLevelData.onRestart) {
      this.inBattleLevelData.onRestart(this);
    }
    if (this.scene.key === 'CastleScene') {
      this.focusOnActiveRooms(() => {
        this.diableInput = false;
        this.pickupUnit(
          this.pieces.getChildren().find(piece => piece.color === 'blue')
        );
      });
    } else {
      this.setZoomLevel();
      this.disableInput = false;
      this.pickupUnit(
        this.pieces.getChildren().find(piece => piece.color === 'blue')
      );
    }
    menuScene.hideKeyItem();
    setTimeout(() => {
      this.disableInput = false;
    }, 0);
  };
  levelCompletedResults(cb = () => {}) {
    const levelData =
      (this.battlingInWorld && this.inBattleLevelData) || this.levelData;
    const combatLevel = levelData.combat && !this.retry;
    const levelIndex = this.inZone
      ? this.inZone.to.index
      : this.dataIndex || this.freeplayLevelIndex;
    const scoreLevelId =
      levelData.scoreLevelId != null ? levelData.scoreLevelId : levelIndex;
    const scoreLocalKey =
      levelData.scoreLocalKey || (levelIndex != null ? String(levelIndex) : '');
    const scoreMode = levelData.scoreMode || 'story';
    const timeBattleEnded = new Date().getTime();
    const matchTimeSeconds = Math.floor(
      (timeBattleEnded - this.timeLevelStarted) / 1000
    );

    //items obtained
    const pieces = this.pieces.getChildren();
    const bluePieceItemIds = pieces
      .filter(piece => piece.color === 'blue' && piece.itemID)
      .reduce((acc, piece) => {
        acc[piece.id] = piece.itemID;
        return acc;
      }, {});
    const startingBlueItemCount = Object.values(this.itemsAtStart).filter(
      itemId => !!itemId
    ).length;
    const ugcItemsUsed = startingBlueItemCount + this.numGroundItemsPickedUp;
    let newlyObtainedItems = [];
    const itemsChanged =
      JSON.stringify(this.itemsAtStart) !== JSON.stringify(bluePieceItemIds);
    if (itemsChanged) {
      const shouldPersistObtainedItems =
        !this.freeplayHighscore &&
        (!this.replay || this.persistObtainedItemsOnReplay);
      Object.keys(bluePieceItemIds).forEach(pieceId => {
        const changed =
          bluePieceItemIds[pieceId] !== this.itemsAtStart[pieceId];
        if (changed) {
          if (shouldPersistObtainedItems) {
            equipWeaponToPieceByID(
              ALL_ITEMS[bluePieceItemIds[pieceId]],
              parseInt(pieceId)
            );
          }
          newlyObtainedItems.push(bluePieceItemIds[pieceId]);
        }
      });
    }
    if (
      !this.replay &&
      !this.freeplayHighscore &&
      !levelData.deferCompletionPersistence
    ) {
      this.persistCampaignLevelCompletion(levelIndex);
    } else if (this.freeplayHighscore) {
      recordEvent(
        {
          event_name: 'level_complete',
          level_id: levelIndex,
          mode: 'freeplay'
        },
        this.game
      );
    }
    if (combatLevel && !this.replay) {
      this.game.soundManager.playSfx('battleResults');
      const resultButtons = this.sharedLevelMode
        ? [
            {
              name: 'steam',
              text: 'Get the Game',
              width: 112,
              height: 20,
              action: () => {
                window.open(
                  'https://store.steampowered.com/app/1624840/Super_Chesslike_Adventure/',
                  '_blank'
                );
              }
            },
            {
              name: 'discord',
              text: 'Discord',
              width: 84,
              height: 20,
              action: () => {
                window.open('https://discord.gg/chesslike', '_blank');
              }
            }
          ]
        : this.freeplayHighscore
        ? [
            {
              name: 'main_menu',
              text: 'Main Menu',
              width: 96,
              height: 20,
              action: this.gotoMainMenu
            },
            {
              name: 'freeplay',
              text: 'Freeplay',
              width: 84,
              height: 20,
              action: this.returnToFreeplayFromResults
            }
          ]
        : null;
      const menuOptions = resultButtons
        ? createFreeplayBattleResultsControllerMenuOptions()
        : createBattleResultsControllerMenuOptions();
      this.currentControllerMenuOptions = menuOptions;
      this.scene.launch('StoryResultsScene', {
        stats: {
          turnIndex: this.game.registry.get('history_index'),
          captured: this.captured,
          undosUsed: this.numUndosUsed,
          itemsUsed: levelData.ugcLevelId
            ? ugcItemsUsed
            : startingBlueItemCount,
          timeSeconds: matchTimeSeconds,
          parStats: levelData.parStats
        },
        menuOptions,
        resultButtons,
        initialButtonName: this.sharedLevelMode
          ? 'steam'
          : resultButtons
          ? 'freeplay'
          : 'continue',
        highlightButtonByName: this.highlightButtonByName,
        isControllerInput: this.game.controllerInput,
        newlyObtainedItems,
        levelData,
        levelIndex,
        scoreLevelId,
        scoreLocalKey,
        scoreMode,
        done: afterClose => {
          this.scene.stop('StoryResultsScene');
          this.clearCurrentMenuOptions();
          if (afterClose) {
            afterClose();
          } else if (cb) {
            cb();
          }
        }
      });
    } else {
      cb && cb();
    }
  }

  persistCampaignLevelCompletion(levelIndex) {
    saveWorldProgress(levelIndex);
    saveStoryProgress(this, levelIndex);
    recordEvent(
      {
        event_name: 'level_complete',
        level_id: levelIndex,
        mode: 'campaign'
      },
      this.game
    );
  }

  endingScene(condition = 'Level Complete') {
    this.levelCompletedResults(() => {
      this.endGame(condition, true);
    });
  }

  checkEndGame() {
    let blue = 0;
    let red = 0;
    this.pieces.getChildren().forEach(piece => {
      if (piece.color === 'blue') {
        blue += 1;
      } else if (piece.color === 'red') {
        red += 1;
      }
    });
    if (blue === 0) {
      this.endGame('You Lost all your pieces!', false);
      return true;
    }
    if (red === 0 && !this.hasExit) {
      this.endingScene("You've captured all the enemy pieces!");
      return true;
    }
    return false;
  }

  checkReplay() {
    const { storyProgress } = loadProgress();
    const { alwaysReplay } = this.levelData;
    const beaten =
      storyProgress &&
      storyProgress.split('-').includes(this.dataIndex.toString());
    return !this.freeplayHighscore && (beaten || alwaysReplay);
  }

  hitDoor() {
    this.endingScene('Exit Reached!');
    return true;
  }

  bouncebackMoveUnit = (
    unit,
    pieceTile,
    origin = null,
    skipNext = null,
    inZone = null
  ) => {
    let bounceBackTile = determineBounceBack(unit.tile, pieceTile, this.map);
    const destinationIndex = pieceTile.index;
    const npc = destinationIndex === WALKABLE;
    if (npc) {
      // tile action...
      if (pieceTile.action) {
        skipNext = true;
        this.resetCurrentPieceMovementAnimation();
      }
    } else {
      const pieceHasHP =
        pieceTile.occupant &&
        (pieceTile.occupant.hp || pieceTile.occupant.hp === 0) &&
        !(pieceTile.occupant === unit);
      if (pieceHasHP) {
        pieceTile.occupant.hp -= 1;
        this.dimPieceAbility(pieceTile.occupant);
        // this.showHitPointsUI(pieceTile, pieceTile.occupant.hp);
      }
    }

    if (bounceBackTile) {
      this.tweens.add({
        targets: unit.parentContainer,
        x: bounceBackTile.pixelX + 48,
        y: bounceBackTile.pixelY + 32,
        ease: 'Back.easeOut',
        duration: 200,
        onComplete: () => {
          const animSwitchOpen =
            pieceTile.occupant &&
            pieceTile.occupant.texture.key === 'anim_switch_off';
          const worldSwitch = (this.switchLocations || []).find(
            location => location.x === pieceTile.x && location.y === pieceTile.y
          );
          const authoredSwitch = this.handleAuthoredSwitchHit(pieceTile);
          if (authoredSwitch || worldSwitch || animSwitchOpen) {
            this.game.soundManager.playSfx('forestSwitchOn');
          } else {
            this.game.soundManager.playSfx('switchImpact');
          }
          let nextTileIndex = unit.tile.index;
          this.map.removeTileAt(origin.x, origin.y, false, true, 'logic_layer');
          bounceBackTile = this.map.putTileAt(
            nextTileIndex,
            bounceBackTile.x,
            bounceBackTile.y,
            true,
            'logic_layer'
          );
          bounceBackTile.occupant = unit;
          unit.tile = bounceBackTile;

          if (this.sys.config === 'WorldScene' && !this.inZone) {
            savePlayerPosition({
              position: { x: bounceBackTile.x, y: bounceBackTile.y }
            });
          }

          if (this.sys.config === 'CastleScene') {
            this.updateActiveRooms();
          }

          History.saveMapState(this, unit.color);

          // Next Turn
          if (!skipNext) {
            // next steps
            if (unit.color === 'blue') {
              this.previousSelectedPiece = unit;
            }
            this.checkNextTurn(bounceBackTile);
          } else {
            if (npc && pieceTile.action) {
              this.inCutscene = true;
              pieceTile.action(this, () => {
                this.inCutscene = false;
                if (this.sys.config !== 'CastleScene') {
                  const nextZoom =
                    this.sys.config === 'WorldScene'
                      ? this.worldZoomLevel
                      : this.getZoomLevel();
                  this.cameras.main.zoomTo(nextZoom, 850, 'Expo.easeOut');
                }
                // next steps
                if (unit.color === 'blue') {
                  this.previousSelectedPiece = unit;
                  if (this.player) {
                    this.previousSelectedPiece = this.player;
                  }
                }
                this.checkNextTurn(bounceBackTile);
              });
            }
          }
        }
      });
    }
  };

  itemTurn(item, origin, target, cb) {
    if (this.abilityButton) {
      this.abilityButton.setStateInactive();
    }
    if (this.currentSelectedPiece.itemCharges) {
      this.currentSelectedPiece.itemCharges -= 1;
      if (this.currentSelectedPiece.itemCharges < 1) {
        this.hideAbilityButton();
      }
    }

    const itemFire = () => {
      this.disableInput = true;

      // store planted tree tile so we can clean it up after battle end
      if (!this.plantedTrees) {
        this.plantedTrees = [];
      }
      const { x, y } = target;

      this.plantedTrees.push({
        x,
        y,
        below_player: this.map.getTileAt(x, y, false, 'below_player')?.index,
        above_player: this.map.getTileAt(x, y - 1, false, 'above_player')
          ?.index,
        logc_layer: this.map.getTileAt(x, y, false, 'logc_layer')?.index
      });

      item.fire(this, origin, target, () => {
        const ended = this.checkEndGame();
        if (!ended) {
          this.selectedArrow && this.selectedArrow.setAlpha(0);
          this.previousSelectedPiece = this.currentSelectedPiece;
          History.saveMapState(this, this.currentSelectedPiece.color);
          this.checkNextTurn(origin);
          if (cb) {
            cb();
          }
        }
      });
    };

    const levelData = this.inBattleLevelData || this.levelData;
    if (levelData && levelData.beforeMove) {
      levelData.beforeMove(this, origin.occupant, target, itemFire);
    } else {
      itemFire();
    }
  }

  async moveUnitToTile(
    unit,
    pieceTile,
    origin = null,
    skipNext = null,
    inZone = null
  ) {
    pieceTile = this.getLogicTile(pieceTile);
    origin = this.getLogicTile(origin);
    unit.init = false;
    const destinationIndex = pieceTile.index;
    let madeKill = BLUE_PIECES.concat(COLORLESS_PIECES, RED_PIECES).includes(
      pieceTile.index
    );
    const hitItem = ITEMS.includes(pieceTile.index);
    const isWarp = destinationIndex === WARP;
    const gameMenu = this.scene.get('GameMenuScene');

    if (pieceTile.goal && unit.color === 'blue') {
      // custom goal sound for scene
      let goalSound = 'door';
      const levelData = this.inBattleLevelData || this.levelData;
      if (levelData && levelData.goalSound) {
        goalSound = levelData.goalSound;
      }
      this.game.soundManager.playSfx(goalSound);
      skipNext = this.hitDoor(pieceTile, unit);
      // skipNext = true;
    }

    if (hitItem) {
      if (isWarp) {
        pieceTile = this.map.getTileAt(
          pieceTile.warpTo.x,
          pieceTile.warpTo.y,
          true,
          'logic_layer'
        );
        // TODO - warp animation here...
        unit.setPosition(pieceTile.pixelX + 48, pieceTile.pixelY + 32);
      } else {
        await this.handleItem(unit, pieceTile);
      }
    }
    let lastTileIndex = pieceTile.index;
    let nextTileIndex = unit.tile.index;
    let lastTilePosition = origin;
    let lastPieceTileSprite = pieceTile.occupant;
    this.resetTile(lastTilePosition);
    pieceTile.index = nextTileIndex;
    this.map.putTileAt(
      nextTileIndex,
      pieceTile.x,
      pieceTile.y,
      true,
      'logic_layer'
    );
    pieceTile.occupant = unit;
    unit.tile = pieceTile;

    // item.onMove hook
    const item = ALL_ITEMS[unit.itemID];
    if (item && item.onMove) {
      await item.onMove(this, unit, pieceTile, origin);
    }

    if (madeKill || hitItem) {
      if (!isWarp) {
        let deadPiece = lastPieceTileSprite;
        if (deadPiece) {
          if (!hitItem) {
            const deadColor = unit.color === 'blue' ? 'red' : 'blue';
            this.captured[deadColor].push({
              index: lastTileIndex,
              id: deadPiece.id,
              turnIndex: gameMenu.turnNumber
            });
          }
          if (this.previousSelectedPiece === deadPiece) {
            this.previousSelectedPiece = null;
          }
          if (this.currentSelectedPiece === deadPiece) {
            this.currentSelectedPiece = null;
          }
          deadPiece.parentContainer?.destroy();
        }
        let ended = this.checkEndGame();
        if (ended) {
          return;
        }
      }
    }

    // above doors covering
    let belowPlayerTile = this.map.getTileAt(
      pieceTile.x,
      pieceTile.y + 1,
      true,
      'below_player'
    );
    if (belowPlayerTile && DOORS.includes(belowPlayerTile.index)) {
      this.map.putTileAt(
        belowPlayerTile.index - 18,
        pieceTile.x,
        pieceTile.y,
        true,
        'shade_layer'
      );
    }
    if (
      this.sys.config === 'WorldScene' &&
      !this.inZone &&
      pieceTile.occupant.color === 'blue'
    ) {
      savePlayerPosition({
        position: { x: pieceTile.x, y: pieceTile.y }
      });
    }
    // console.log('end of game mvoeUnitToTile...', this.inZone);
    if (!skipNext) {
      // next steps
      if (unit.color === 'blue') {
        this.previousSelectedPiece = unit;
      }
      History.saveMapState(this, unit.color);
      this.checkNextTurn(pieceTile);
    }
  }

  resetTile(tilePosition) {
    let lastTile = this.map.getTileAt(
      tilePosition.x,
      tilePosition.y,
      true,
      'logic_layer'
    );
    //figure when tile was before it was last occupied...
    let oldTileIndex = -1;
    if (lastTile.goal) {
      oldTileIndex = LOGIC_DOOR;
    }
    if (lastTile.warp) {
      oldTileIndex = WARP;
    }
    if (lastTile.interactable) {
      oldTileIndex = WALKABLE;
    }
    this.map.putTileAt(
      oldTileIndex,
      lastTile.x,
      lastTile.y,
      true,
      'logic_layer'
    );

    lastTile.occupant = null;
    const shade = this.map.getTileAt(
      lastTile.x,
      lastTile.y,
      true,
      'shade_layer'
    );
    if (shade && shade.index !== SHADOW) {
      this.map.removeTileAt(
        lastTile.x,
        lastTile.y,
        false,
        false,
        'shade_layer'
      );
    }
  }

  async checkNextTurn(lastMoveTile, pieces) {
    let piecesGroup = pieces || this.pieces;
    const lastMovedPiece = lastMoveTile && lastMoveTile.occupant;
    const lastMovedColor = (lastMovedPiece && lastMovedPiece.color) || 'red';
    const enemyColor = lastMovedColor === 'red' ? 'blue' : 'red';
    const enemyPresent = piecesGroup
      .getChildren()
      .find(piece => piece.color === enemyColor);
    let blueTurn = lastMovedColor !== 'blue' || !enemyPresent;
    const menuScene = this.scene.get('GameMenuScene');
    window.dispatchEvent(
      new CustomEvent('checkNextTurn', {
        detail: {
          scene: this,
          lastMovedPiece,
          lastMovedColor
        }
      })
    );
    if (!blueTurn) {
      menuScene.setTurnColor('red');
      this.disableInput = true;
      this.getCpuMove('red');
    } else {
      menuScene.setTurnColor('blue');
      menuScene.setTurnNumber(menuScene.turnNumber + 1);
      if (this.cpuSimulation) {
        this.getCpuMove('blue');
      } else {
        this.playerTurn();
      }
    }
  }
  getCpuMove(color) {
    SharedAI.determineMove(this.map, color, this.scene.key !== 'CastleScene');
  }
  playerTurn() {
    this.staleMateCounter = (this.staleMateCounter || 0) + 1;
    const pieces = this.pieces.getChildren();
    const numBlue = pieces.filter(piece => piece.color === 'blue').length;
    const numRed = pieces.filter(piece => piece.color === 'red').length;

    if (this.staleMateCounter > 4 && numRed > 0) {
      this.staleMateCounter = 0;
      if (!this.battlingInWorld && this.levelData?.story?.toString() === '0') {
        this.endGame(`It's a stalemate`, false, false);
      } else {
        console.log('Game.playerTurn staleMateCounter', this.staleMateCounter);
        this.gameOver();
      }
      return;
    }

    // check for possible moves and otherwise hand over to CPU
    if (
      (this.battlingInStory || this.battlingInWorld) &&
      numBlue > 0 &&
      numRed > 0 &&
      !SharedAI.hasValidMoves(
        this.map,
        'blue',
        this.scene.key !== 'CastleScene'
      )
    ) {
      // console.log('Game.playerturn no blue moves, checking next turn...');
      this.checkNextTurn({ occupant: { color: 'blue' } });
      return;
    }

    this.staleMateCounter = 0;
    if (
      this.currentActiveRooms &&
      this.currentActiveRooms.rooms.length &&
      this.previousSelectedPiece &&
      this.focusOnRoomWithPiece
    ) {
      this.focusOnRoomWithPiece(this.previousSelectedPiece, () => {
        this.disableInput = false;
        this.pickupUnit(this.previousSelectedPiece);
      });
    } else {
      this.disableInput = this.inCutscene ? true : false;
      this.previousSelectedPiece && this.pickupUnit(this.previousSelectedPiece);
    }
  }
  pcMove(move) {
    let tile = move.piece.tile || move.piece;
    let battleZone = { x: 0, y: 0, w: this.map.width, h: this.map.height };
    if (this.inZone) {
      battleZone = this.inZone.rect;
    }

    tile = this.map.getTileAt(
      battleZone.x + tile.x,
      battleZone.y + tile.y,
      true,
      'logic_layer'
    );
    let movingSprite = this.pieces.getChildren().find(piece => {
      return piece.tile && piece.tile.x === tile.x && piece.tile.y === tile.y;
    });
    let pieceTile = this.map.getTileAt(
      battleZone.x + move.move.x,
      battleZone.y + move.move.y,
      true,
      'logic_layer'
    );
    if (this.currentActiveRooms && this.currentActiveRooms.rooms.length > 1) {
      this.focusOnRoomWithPiece(movingSprite, () => {
        this.animateMove(movingSprite, pieceTile, tile);
      });
    } else {
      this.animateMove(movingSprite, pieceTile, tile);
    }
  }

  pickupUnit = gameObject => {
    if (this.disableInput) {
      return;
    }
    this.currentSelectedPiece = gameObject;
    this.highlightUnitMoveset(this.currentSelectedPiece);
    if (gameObject.itemID && ALL_ITEMS[gameObject.itemID].preMoveButton) {
      if (this.battlingInWorld || this.battlingInStory) {
        this.showAbilityButtonOnPiece(gameObject);
      }
    }
  };

  showAbilityButtonOnPiece(pieceSprite) {
    if (this.abilityButton) {
      const alreadySetOnPiece =
        this.abilityButton.x === pieceSprite.parentContainer.x &&
        this.abilityButton.y === pieceSprite.parentContainer.y;
      const chargesCheck =
        pieceSprite.itemCharges || pieceSprite.itemCharges === undefined;
      if (chargesCheck) {
        if (!alreadySetOnPiece) {
          this.abilityButton.setPosition(
            pieceSprite.parentContainer.x,
            pieceSprite.parentContainer.y
          );
          const item = ALL_ITEMS[pieceSprite.itemID];
          this.abilityButton.setItem(item, pieceSprite);
          this.abilityButton.setAlpha(1);
        } else {
          this.abilityButton.setAlpha(1);
        }
        if (
          !this.abilityButton.alpha &&
          !(
            ['WorldScene', 'StoryLevelScene', 'CastleLevelScene'].includes(
              this.sys.config
            ) && !this.battlingInWorld
          )
        ) {
          this.abilityButton.setAlpha(1);
        }
      }
    }
  }

  hideAbilityButton() {
    if (this.abilityButton) {
      this.abilityButton.setAlpha(0);
    }
  }

  highlightUnitMoveset(gameObject) {
    if (!gameObject || !gameObject.tile) {
      return;
    }
    this.currentMoveSet = Movement.getPieceMoveSet(
      gameObject.tile,
      this.map,
      this.map.width,
      this.game.registry.get('hasKey'),
      gameObject && gameObject.ss
    );
    const enemySelected = gameObject && gameObject.color === 'red';
    Movement.clearTileHighlighting(this.map, this.inZone && this.inZone.rect);
    // Movement.setTileSelected(gameObject.tile, this.map);
    // this.selectedArrow && this.selectedArrow.setAlpha(1);
    setTimeout(() => {
      if (
        !this.isMoving &&
        !this.inCutscene &&
        this.map &&
        gameObject &&
        gameObject.tile
      ) {
        Movement.setTileSelected(gameObject.tile, this.map);
        this.selectedArrow && this.selectedArrow.setAlpha(1);
      }
    }, 300);
    this.selectedArrow &&
      gameObject.parentContainer &&
      this.selectedArrow.setPosition(
        gameObject.parentContainer.x,
        gameObject.parentContainer.y - 20
      );
    const inWorldAndNotBattling =
      this.scene.key === 'WorldScene' && !this.battlingInWorld;
    const inReplayFreeRoam =
      this.replay &&
      ['StoryLevelScene', 'CastleScene'].includes(this.scene.key);
    if (!inWorldAndNotBattling && !inReplayFreeRoam) {
      Movement.setTilesHighlighted(
        gameObject.tile,
        this.currentMoveSet.moveSet,
        this.map,
        enemySelected
      );

      Movement.setTilesTargeted(
        this.currentMoveSet.attackSet,
        this.map,
        enemySelected
      );
    }
  }

  dropUnit = (pointer, gameObject, controllerActivated) => {
    if (this.disableInput) {
      return;
    }
    let pointerTileX, pointerTileY;
    if (controllerActivated) {
      pointerTileX = this.map.worldToTileX(this.cursorPointer.x);
      pointerTileY = this.map.worldToTileY(this.cursorPointer.y);
    } else {
      var worldPoint = pointer.positionToCamera(this.cameras.main);
      pointerTileX = this.map.worldToTileX(worldPoint.x);
      pointerTileY = this.map.worldToTileY(worldPoint.y);
    }
    let pieceTile = this.map.getTileAt(
      gameObject.tile.x,
      gameObject.tile.y,
      true,
      'logic_layer'
    );

    let nextPieceTile = this.map.getTileAt(
      pointerTileX,
      pointerTileY,
      true,
      'logic_layer'
    );
    if (gameObject.color === 'blue') {
      const canMoveThere =
        this.currentMoveSet &&
        (this.currentMoveSet.moveSet.includes(nextPieceTile) ||
          this.currentMoveSet.attackSet.includes(nextPieceTile));
      if (canMoveThere) {
        this.isMoving = true;
        this.hideAbilityButton();
        const levelData = this.inBattleLevelData || this.levelData;
        if (levelData && levelData.beforeMove) {
          levelData.beforeMove(this, pieceTile.occupant, nextPieceTile, () => {
            this.animateDroppedUnitMove(
              gameObject,
              nextPieceTile,
              pieceTile,
              false,
              () => {
                this.isMoving = false;
              }
            );
          });
        } else {
          this.animateDroppedUnitMove(
            gameObject,
            nextPieceTile,
            pieceTile,
            false,
            () => {
              this.isMoving = false;
            }
          );
        }
      } else {
        const inWorldAndNotBattling =
          this.scene.key === 'WorldScene' && !this.battlingInWorld;
        const inReplayFreeRoam =
          this.replay &&
          ['StoryLevelScene', 'CastleScene'].includes(this.scene.key);
        if (this.multiMove && (inWorldAndNotBattling || inReplayFreeRoam)) {
          this.selectedArrow && this.selectedArrow.setAlpha(0);
          Movement.clearTileHighlighting(this.map);
          this.multiMove(nextPieceTile, gameObject);
        } else {
          this.deselectCurrentPiece();
        }
      }
    } else {
      this.deselectCurrentPiece();
    }
  };
  deselectCurrentPiece() {
    const numBluePieces =
      this.pieces.getChildren().reduce((acc, val) => {
        if (val.color === 'blue') {
          acc += 1;
        }
        return acc;
      }, 0) > 1;
    if (this.currentSelectedPiece && numBluePieces) {
      this.selectedArrow && this.selectedArrow.setAlpha(0);
      this.hideAbilityButton();
      this.currentSelectedPiece = null;
      Movement.clearTileHighlighting(this.map);
      Movement.setTilesSelectable(this.pieces, this.map);
    }
  }
  animateDroppedUnitMove(
    gameObject,
    nextPieceTile,
    pieceTile,
    directControlMovement = false,
    cb = () => {}
  ) {
    this.isMoving = true;
    this.animateMove(
      gameObject,
      nextPieceTile,
      pieceTile,
      directControlMovement,
      null,
      cb
    );
    this.selectedArrow && this.selectedArrow.setAlpha(0);
    Movement.clearTileHighlighting(this.map, this.inZone && this.inZone.rect);
    this.previousSelectedPiece = this.currentSelectedPiece;
    this.currentSelectedPiece = null;
  }
  startInput() {
    this.setEventListeners();
    const pieces = this.pieces.getChildren();
    const bluePieces = pieces.filter(piece => piece.color === 'blue');
    const defaultFirstPiece = bluePieces[0];
    if (bluePieces.length === 1 && defaultFirstPiece) {
      this.pickupUnit(defaultFirstPiece);
    } else {
      this.hideAbilityButton();
      this.selectedArrow && this.selectedArrow.setAlpha(0);
      this.currentSelectedPiece = null;
      Movement.clearTileHighlighting(this.map);
      Movement.setTilesSelectable(this.pieces, this.map);
    }
  }
  update() {
    const cam = this.cameras.main;
    const moveSpeed = 4;
    const partyMenu = this.scene.get('PartyMenu');
    const partyMenuActive = this.scene.isActive('PartyMenu');
    if (this.map && this.map.layer) {
      var worldPoint = this.input.activePointer.positionToCamera(
        this.cameras.main
      );
      if (
        (!partyMenu || !partyMenu.visible) &&
        !this.currentControllerMenuOptions
      ) {
        controllerInputUpdate(this);
      } else if (partyMenuActive && partyMenu && partyMenu.visible) {
        return;
      }

      const dialogueScene = this.scene.get('DialogueScene');
      const dialogueActive = dialogueScene.scene.isActive();
      if (dialogueActive && this.currentControllerMenuOptions) {
        return controllerInputForMenuDirections(
          dialogueScene,
          dialogueScene.moveInDirection
        );
      }

      // if (this.currentControllerMenuOptions) {
      controllerInputForMenuDirections(this, this.moveInDirection);
      // }

      // Rounds down to nearest tile
      let pointerTileX = this.map.worldToTileX(worldPoint.x);
      let pointerTileY = this.map.worldToTileY(worldPoint.y);

      if (this.game.controllerInput && this.cursorPointer) {
        pointerTileX = this.map.worldToTileX(this.cursorPointer.x);
        pointerTileY = this.map.worldToTileY(this.cursorPointer.y);
      }

      const groundTile = this.map.getTileAt(
        pointerTileX,
        pointerTileY,
        true,
        'ground_layer'
      );
      const highlightTile = this.map.getTileAt(
        pointerTileX,
        pointerTileY,
        true,
        'highlight_layer'
      );
      const logicTile = this.map.getTileAt(
        pointerTileX,
        pointerTileY,
        true,
        'logic_layer'
      );

      // highlight path to cursor
      if (
        (this.scene.key === 'WorldScene' || this.replay) &&
        !this.inCutscene
      ) {
        if (groundTile && groundTile.index > -1) {
          if (logicTile.index !== UNWALKABLE) {
            const pathingPiece = this.currentSelectedPiece || this.player;
            if (pathingPiece && !this.battlingInWorld && !this.disableInput) {
              if (
                !this.lastPathToTile ||
                this.lastPathToTile.x !== groundTile.x ||
                this.lastPathToTile.y !== groundTile.y
              ) {
                this.lastPathToTile = { x: groundTile.x, y: groundTile.y };
                const path =
                  pathingPiece.scene &&
                  pathingPiece.tile.index !== -1 &&
                  AI.Astar(pathingPiece, pathingPiece.tile, groundTile);
                Movement.clearTileHighlighting(this.map);
                if (path) {
                  Movement.setTilesHighlightedForMultimove(path, this.map);
                }
              }
            }
          } else {
            this.lastPathToTile = null;
            if (!this.inZone) {
              Movement.clearTileHighlighting(this.map);
            }
          }
        } else {
          this.lastPathToTile = null;
          if (!this.inZone) {
            Movement.clearTileHighlighting(this.map);
          }
        }
      }

      //adjust highlighting for more specific art
      if (this.lastTileHighlighted) {
        const lastHighlightTile = this.map.getTileAt(
          this.lastTileHighlighted[0],
          this.lastTileHighlighted[1],
          true,
          'highlight_layer'
        );
        lastHighlightTile.index = lastHighlightTile.currentHighlight;
        this.lastTileHighlighted = null;
      }
      if (
        highlightTile &&
        HIGHLIGHTABLES.includes(highlightTile.index) &&
        (!logicTile ||
          !logicTile.occupant ||
          logicTile.occupant !== this.currentSelectedPiece)
      ) {
        let nextHighlight = TARGET_TILE;
        if (HIGHLIGHT_NPC === highlightTile.index) {
          nextHighlight = HIGHLIGHT_NPC_HOVER;
        }
        if (HIGHLIGHT_DOOR === highlightTile.index) {
          nextHighlight = HIGHLIGHT_DOOR_HOVER;
        }
        if (HIGHLIGHT_STAIRS_UP === highlightTile.index) {
          nextHighlight = HIGHLIGHT_STAIRS_UP_HOVER;
        }
        if (HIGHLIGHT_STAIRS_DOWN === highlightTile.index) {
          nextHighlight = HIGHLIGHT_STAIRS_DOWN_HOVER;
        }
        highlightTile.index = nextHighlight;
        if (
          (this.scene.key === 'WorldScene' && !this.battlingInWorld) ||
          this.replay
        ) {
          highlightTile.currentHighlight = nextHighlight;
        }
        this.lastTileHighlighted = [highlightTile.x, highlightTile.y];
      } else {
        this.lastTileHighlighted = null;
      }

      if (this.projectileAiming) {
        Movement.setTilesHighlightedForProjectile(
          this.projectileTargets,
          this.map
        );
        if (highlightTile) {
          const isSelectable = this.projectileTargets.find(
            tile => tile.x === highlightTile.x && tile.y === highlightTile.y
          );
          if (isSelectable) {
            Movement.setTileSelected(highlightTile, this.map);
          }
        }
      }
    }

    if (this.hasEditingTools) {
      if (this.cursors) {
        if (this.cursors.down.isDown) {
          cam.scrollY += moveSpeed;
          // console.log('CAM Y:', cam.scrollY, cam.midPoint);
        } else if (this.cursors.up.isDown) {
          cam.scrollY -= moveSpeed;
          // console.log('CAM Y:', cam.scrollY, cam.midPoint);
        }

        if (this.cursors.left.isDown) {
          cam.scrollX -= moveSpeed;
          // console.log('CAM X:', cam.scrollX, cam.midPoint);
        } else if (this.cursors.right.isDown) {
          cam.scrollX += moveSpeed;
          // console.log('CAM X:', cam.scrollX, cam.midPoint);
        }
      }
    }
  }
}
